/**
 * Elite Batch 14 Part 3 — Workflow Templates 1288–1307
 * Calendar task creation, call transcript risk detection, change request approvals,
 * client feedback bots, project updates, community registration, company news digests,
 * OKR tracking, compliance checking, contact updating, contract workflows,
 * dependency tracking, and customer churn risk alerting.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 14 PART 3 — TEMPLATES 1288–1307
// =============================================================================

export const ELITE_BATCH14_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1288. CALENDAR TO TASK CREATOR (7 nodes)
  // ===================================================================

  "Calendar to Task Creator": {
    summary:
      "Triggers when a meeting ends via webhook, uses AI to extract meeting notes and action items, validates action items with a condition, creates tasks in Asana, notifies assignees in Slack, and emails the meeting organizer a summary.",
    nodes: [
      trigger("Webhook: Meeting ended event", "webhook_app", {
        source: "google_calendar",
        event: "meeting.ended",
        howItStarts:
          "Fires when a calendar meeting ends and includes meeting notes, attendees, duration, and recording link from Google Calendar or similar calendar provider",
        setup:
          "Configure webhook from Google Calendar or your calendar provider to fire on meeting end events. Set OpenAI API key for note extraction. Provide Asana personal access token and project GID. Configure Slack webhook URL for notifications.",
        inputFields: [],
      }),

      // 2. AI extract meeting notes
      aiNode(
        "2",
        "Extract meeting notes and context",
        280,
        "You are a meeting notes parser. Extract the meeting title, organizer email, attendee emails, meeting notes or transcript text, duration, and date from the raw webhook payload regardless of field naming conventions. Respond JSON: {meeting_title: string, organizer_email: string, attendee_emails: string, notes_text: string, duration_minutes: number, meeting_date: string}",
        "Raw meeting webhook data: {webhook_payload}",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "organizer_email", as: "organizer_email", type: "string" },
          { field: "attendee_emails", as: "attendee_emails", type: "string" },
          { field: "notes_text", as: "notes_text", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
        ],
      ),

      // 3. AI action item extraction
      aiNode(
        "3",
        "Extract action items from notes",
        520,
        "You are an action item extractor. From the meeting notes, identify all concrete action items with assigned owners and due dates. Each action item should have a clear task description, the person responsible (from the attendee list), and a suggested due date. Respond JSON: {action_items: string, action_count: number, has_action_items: string, priority_tasks: string, assignee_summary: string}",
        "Meeting: {meeting_title}. Notes: {notes_text}. Attendees: {attendee_emails}. Date: {meeting_date}",
        [
          { field: "action_items", as: "action_items", type: "string" },
          { field: "action_count", as: "action_count", type: "number" },
          { field: "has_action_items", as: "has_action_items", type: "string" },
          { field: "priority_tasks", as: "priority_tasks", type: "string" },
          { field: "assignee_summary", as: "assignee_summary", type: "string" },
        ],
      ),

      // 4. Condition: has action items?
      n("4", "condition", "Has action items?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{has_action_items}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 5. Create Asana tasks (true path)
      n("5", "hitApi", "Create Asana tasks", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Follow-up: {meeting_title}","notes":"{action_items}","projects":["{asana_project_gid}"],"due_on":"{meeting_date}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "task_gid", type: "string" },
            { name: "data.name", customName: "task_name", type: "string" },
            {
              name: "data.permalink_url",
              customName: "task_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Notify assignees in Slack
      slackNode(
        "6",
        "Notify assignees in Slack",
        1240,
        ":clipboard: *New Action Items from {meeting_title}*\n\n{action_count} tasks created from today's meeting.\n\nPriority Tasks:\n{priority_tasks}\n\nAssignees:\n{assignee_summary}\n\nView in Asana: {task_url}",
      ),

      // 7. Email meeting organizer summary
      emailNode(
        "7",
        "Email organizer meeting summary",
        1480,
        "{organizer_email}",
        "Meeting Summary: {meeting_title} — {action_count} Action Items Created",
        'Hi,\n\nYour meeting "{meeting_title}" on {meeting_date} ({duration_minutes} min) has been processed.\n\n{action_count} action items were identified and created as tasks in Asana.\n\nAction Items:\n{action_items}\n\nAssignment Summary:\n{assignee_summary}\n\nView tasks: {task_url}\n\nBest,\nWorkflow Automation',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1289. CALL TRANSCRIPT RISK DETECTOR (8 nodes)
  // ===================================================================

  "Call Transcript Risk Detector": {
    summary:
      "Triggers from a call transcript webhook, extracts transcript data with AI, performs risk analysis, classifies severity via condition, sends critical risk alerts to Slack, emails managers on high risk, routes competitor intelligence to a separate Slack channel, and logs all risks to Google Sheets.",
    nodes: [
      trigger("Webhook: Call transcript received", "webhook_app", {
        source: "call_recording",
        event: "transcript.completed",
        howItStarts:
          "Fires when a call recording platform (Gong, Chorus, Otter.ai) completes transcription and delivers the full transcript text, caller info, and call metadata via webhook",
        setup:
          "Configure webhook from your call recording platform (Gong, Chorus, or Otter.ai). Set OpenAI API key for transcript analysis. Configure Slack webhook URLs for risk alerts and competitor intel channels. Provide Google Sheets spreadsheet ID for risk logging.",
        inputFields: [],
      }),

      // 2. AI extract transcript data
      aiNode(
        "2",
        "Extract transcript data",
        280,
        "You are a call transcript parser. Extract the caller name, caller email, call date, call duration, rep name, rep email, customer company name, and the full transcript text from the raw webhook payload regardless of field naming conventions. Respond JSON: {caller_name: string, caller_email: string, call_date: string, call_duration_minutes: number, rep_name: string, rep_email: string, customer_company: string, transcript_text: string}",
        "Raw transcript webhook data: {webhook_payload}",
        [
          { field: "caller_name", as: "caller_name", type: "string" },
          { field: "caller_email", as: "caller_email", type: "string" },
          { field: "call_date", as: "call_date", type: "string" },
          {
            field: "call_duration_minutes",
            as: "call_duration_minutes",
            type: "number",
          },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "customer_company", as: "customer_company", type: "string" },
          { field: "transcript_text", as: "transcript_text", type: "string" },
        ],
      ),

      // 3. AI risk analysis
      aiNode(
        "3",
        "Analyze transcript for risks",
        520,
        "You are a sales call risk analyst. Analyze the transcript for: churn signals (dissatisfaction, cancellation mentions, competitor comparisons), compliance risks (unauthorized promises, pricing deviations, regulatory violations), competitor intelligence (competitor names mentioned, feature comparisons, switching intent), and escalation triggers (angry language, legal threats, executive involvement requests). Score overall risk 0-100. Respond JSON: {risk_score: number, risk_level: string, churn_signals: string, compliance_risks: string, competitor_mentions: string, escalation_triggers: string, has_competitor_intel: string, summary: string, recommended_actions: string}",
        "Transcript from call with {customer_company} on {call_date}. Rep: {rep_name}. Duration: {call_duration_minutes} min.\n\nTranscript:\n{transcript_text}",
        [
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "churn_signals", as: "churn_signals", type: "string" },
          { field: "compliance_risks", as: "compliance_risks", type: "string" },
          {
            field: "competitor_mentions",
            as: "competitor_mentions",
            type: "string",
          },
          {
            field: "escalation_triggers",
            as: "escalation_triggers",
            type: "string",
          },
          {
            field: "has_competitor_intel",
            as: "has_competitor_intel",
            type: "string",
          },
          { field: "summary", as: "risk_summary", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
        ],
      ),

      // 4. Risk severity classification condition
      n("4", "condition", "Is risk critical (score >= 80)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{risk_score}", operator: "gte", value: "80" },
          ],
        },
      }),

      // 5. Critical risk immediate alert Slack (true path)
      slackNode(
        "5",
        "Send critical risk alert to Slack",
        1000,
        ":rotating_light: *CRITICAL RISK DETECTED — Call with {customer_company}*\n\nRisk Score: {risk_score}/100 ({risk_level})\nRep: {rep_name} | Date: {call_date}\n\nChurn Signals: {churn_signals}\nCompliance Risks: {compliance_risks}\nEscalation Triggers: {escalation_triggers}\n\nRecommended Actions:\n{recommended_actions}",
      ),

      // 6. High risk manager notification email (false path — still high enough to notify)
      emailNode(
        "6",
        "Email manager risk notification",
        1000,
        "{rep_email}",
        "Call Risk Alert: {customer_company} — Risk Score {risk_score}/100",
        "Hi {rep_name},\n\nYour call with {customer_company} on {call_date} has been flagged with a risk score of {risk_score}/100.\n\nRisk Level: {risk_level}\n\nSummary:\n{risk_summary}\n\nChurn Signals: {churn_signals}\nCompliance Risks: {compliance_risks}\n\nRecommended Actions:\n{recommended_actions}\n\nPlease review and take appropriate action.\n\nBest,\nRisk Detection System",
        350,
      ),

      // 7. Competitor intelligence routing Slack
      slackNode(
        "7",
        "Post competitor intel to Slack",
        1240,
        ":mag: *Competitor Intelligence — {customer_company} Call*\n\nRep: {rep_name} | Date: {call_date}\nCompetitor Mentions: {competitor_mentions}\n\nThis intel has been logged for competitive analysis.",
      ),

      // 8. Risk log Sheets
      sheetsNode(
        "8",
        "Log risk data to Sheets",
        1480,
        "CallRiskLog",
        '[["{call_date}","{customer_company}","{rep_name}","{risk_score}","{risk_level}","{churn_signals}","{compliance_risks}","{competitor_mentions}","{recommended_actions}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1290. CHANGE REQUEST APPROVAL WORKFLOW (8 nodes)
  // ===================================================================

  "Change Request Approval Workflow": {
    summary:
      "Triggers from a change request webhook, extracts request data with AI, auto-classifies impact using runCode, routes by impact level via condition, sends CAB notification email for high-impact changes, waits 3 days for review, checks vote results via condition, and archives the change record to Sheets.",
    nodes: [
      trigger("Webhook: Change request submitted", "webhook_app", {
        source: "itsm_platform",
        event: "change_request.created",
        howItStarts:
          "Fires when an IT change request is submitted through a service management platform (ServiceNow, Jira Service Management) including the change description, requester info, affected systems, and proposed timeline",
        setup:
          "Configure webhook from your ITSM platform (ServiceNow or Jira Service Management). Set OpenAI API key for request classification. Configure email distribution list for Change Advisory Board. Provide Google Sheets spreadsheet ID for change record archival.",
        inputFields: [],
      }),

      // 2. AI extract request data
      aiNode(
        "2",
        "Extract change request data",
        280,
        "You are an ITSM data parser. Extract the change request title, requester name, requester email, affected systems, change description, proposed start date, proposed end date, rollback plan, business justification, and urgency level from the raw webhook payload regardless of field naming conventions. Respond JSON: {change_title: string, requester_name: string, requester_email: string, affected_systems: string, change_description: string, proposed_start: string, proposed_end: string, rollback_plan: string, business_justification: string, urgency: string}",
        "Raw change request webhook data: {webhook_payload}",
        [
          { field: "change_title", as: "change_title", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "affected_systems", as: "affected_systems", type: "string" },
          {
            field: "change_description",
            as: "change_description",
            type: "string",
          },
          { field: "proposed_start", as: "proposed_start", type: "string" },
          { field: "proposed_end", as: "proposed_end", type: "string" },
          { field: "rollback_plan", as: "rollback_plan", type: "string" },
          {
            field: "business_justification",
            as: "business_justification",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Auto-classification runCode
      n("3", "runCode", "Classify change impact level", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify change request by impact level based on affected systems and urgency
const systems = (vars.affected_systems || "").toLowerCase();
const urgency = (vars.urgency || "").toLowerCase();
const description = (vars.change_description || "").toLowerCase();
const criticalSystems = ["database", "production", "payment", "auth", "core api", "load balancer", "dns", "firewall"];
const highSystems = ["staging", "ci/cd", "monitoring", "backup", "email server"];
const affectedCritical = criticalSystems.filter(s => systems.includes(s));
const affectedHigh = highSystems.filter(s => systems.includes(s));
let impact = "low";
let impact_score = 20;
let requires_cab = "no";
if (affectedCritical.length > 0 || urgency === "critical" || urgency === "emergency") {
  impact = "critical";
  impact_score = 90 + (affectedCritical.length * 5);
  requires_cab = "yes";
} else if (affectedHigh.length > 0 || urgency === "high") {
  impact = "high";
  impact_score = 60 + (affectedHigh.length * 10);
  requires_cab = "yes";
} else if (urgency === "medium") {
  impact = "medium";
  impact_score = 40;
  requires_cab = "no";
}
const hasRollback = (vars.rollback_plan || "").trim().length > 20 ? "yes" : "no";
return {
  impact_level: impact,
  impact_score: Math.min(impact_score, 100),
  requires_cab: requires_cab,
  critical_systems_affected: affectedCritical.join(", ") || "none",
  has_rollback_plan: hasRollback,
  classification_notes: impact === "critical" ? "Critical systems affected — full CAB review required" : impact === "high" ? "High-impact change — CAB review recommended" : "Standard change — auto-approval eligible"
};`,
          outputVariables: [
            { name: "impact_level", type: "string" },
            { name: "impact_score", type: "number" },
            { name: "requires_cab", type: "string" },
            { name: "critical_systems_affected", type: "string" },
            { name: "has_rollback_plan", type: "string" },
            { name: "classification_notes", type: "string" },
          ],
        },
      }),

      // 4. Approval routing condition
      n("4", "condition", "Requires CAB approval?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{requires_cab}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 5. CAB notification email (true path)
      emailNode(
        "5",
        "Send CAB review notification",
        1000,
        "{cab_distribution_email}",
        "CAB Review Required: {change_title} — Impact: {impact_level}",
        "Change Advisory Board Review Required\n\nChange Title: {change_title}\nRequester: {requester_name}\nImpact Level: {impact_level} (Score: {impact_score}/100)\nCritical Systems: {critical_systems_affected}\n\nDescription:\n{change_description}\n\nBusiness Justification:\n{business_justification}\n\nProposed Window: {proposed_start} to {proposed_end}\nRollback Plan Available: {has_rollback_plan}\n\nClassification Notes: {classification_notes}\n\nPlease submit your vote (approve/reject) within 3 business days.\n\nRegards,\nChange Management System",
      ),

      // 6. Delay 3 days for review
      n("6", "delayAction", "Wait 3 days for CAB review", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 7. Vote collection condition
      n("7", "condition", "Change approved by CAB?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{impact_level}",
              operator: "not_equals",
              value: "critical",
            },
          ],
        },
      }),

      // 8. Change record archival Sheets
      sheetsNode(
        "8",
        "Archive change record to Sheets",
        1720,
        "ChangeRequests",
        '[["{change_title}","{requester_name}","{impact_level}","{impact_score}","{affected_systems}","{critical_systems_affected}","{proposed_start}","{proposed_end}","{has_rollback_plan}","{classification_notes}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1291. CHANGE REQUEST FORM (7 nodes)
  // ===================================================================

  "Change Request Form": {
    summary:
      "Triggers from a form submission webhook, extracts change data with AI, assesses impact with AI, scores risk via runCode, creates a Jira ticket for the change request, emails the requester a confirmation, and notifies approvers in Slack.",
    nodes: [
      trigger("Webhook: Change request form submitted", "webhook_form", {
        source: "internal_form",
        event: "form.submitted",
        howItStarts:
          "Fires when an employee submits a change request form through an internal portal, including change description, systems affected, urgency, and business justification",
        setup:
          "Configure webhook from your form builder (Outgrow Forms, Outgrow Forms, internal portal). Set OpenAI API key for impact assessment. Provide Jira API token and project key. Configure Slack webhook for approver notifications.",
        inputFields: [],
      }),

      // 2. AI extract change data
      aiNode(
        "2",
        "Extract change request details",
        280,
        "You are a form data parser. Extract the change title, requester name, requester email, department, systems affected, change type (standard/normal/emergency), proposed implementation date, estimated downtime, rollback plan, and business justification from the raw form submission payload regardless of field naming. Respond JSON: {change_title: string, requester_name: string, requester_email: string, department: string, systems_affected: string, change_type: string, implementation_date: string, estimated_downtime: string, rollback_plan: string, justification: string}",
        "Raw form submission data: {webhook_payload}",
        [
          { field: "change_title", as: "change_title", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "systems_affected", as: "systems_affected", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          {
            field: "implementation_date",
            as: "implementation_date",
            type: "string",
          },
          {
            field: "estimated_downtime",
            as: "estimated_downtime",
            type: "string",
          },
          { field: "rollback_plan", as: "rollback_plan", type: "string" },
          { field: "justification", as: "justification", type: "string" },
        ],
      ),

      // 3. AI impact assessment
      aiNode(
        "3",
        "Assess change impact",
        520,
        "You are an IT change impact analyst. Assess the potential impact of this change request on business operations, users, and infrastructure. Consider the systems affected, downtime, and rollback plan quality. Provide a risk rating and identify any gaps in the change plan. Respond JSON: {risk_rating: string, risk_score: number, user_impact: string, infrastructure_impact: string, rollback_adequacy: string, missing_details: string, approval_recommendation: string}",
        "Change: {change_title}. Type: {change_type}. Systems: {systems_affected}. Downtime: {estimated_downtime}. Rollback Plan: {rollback_plan}. Justification: {justification}.",
        [
          { field: "risk_rating", as: "risk_rating", type: "string" },
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "user_impact", as: "user_impact", type: "string" },
          {
            field: "infrastructure_impact",
            as: "infrastructure_impact",
            type: "string",
          },
          {
            field: "rollback_adequacy",
            as: "rollback_adequacy",
            type: "string",
          },
          { field: "missing_details", as: "missing_details", type: "string" },
          {
            field: "approval_recommendation",
            as: "approval_recommendation",
            type: "string",
          },
        ],
      ),

      // 4. Risk scoring runCode
      n("4", "runCode", "Calculate composite risk score", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate composite risk score from AI assessment and change metadata
const aiScore = parseInt(vars.risk_score || "50", 10);
const changeType = (vars.change_type || "").toLowerCase();
const downtime = (vars.estimated_downtime || "").toLowerCase();
let typeMultiplier = 1.0;
if (changeType === "emergency") typeMultiplier = 1.5;
else if (changeType === "normal") typeMultiplier = 1.2;
else if (changeType === "standard") typeMultiplier = 0.8;
let downtimeBonus = 0;
if (downtime.includes("hour") || downtime.includes("day")) downtimeBonus = 15;
else if (downtime.includes("minute")) downtimeBonus = 5;
const compositeScore = Math.min(Math.round(aiScore * typeMultiplier + downtimeBonus), 100);
const priority = compositeScore >= 75 ? "P1-Critical" : compositeScore >= 50 ? "P2-High" : compositeScore >= 25 ? "P3-Medium" : "P4-Low";
return {
  composite_risk_score: compositeScore,
  jira_priority: priority,
  risk_category: compositeScore >= 50 ? "high_risk" : "standard",
  ticket_summary: vars.change_title + " [" + priority + "] — " + vars.change_type + " change"
};`,
          outputVariables: [
            { name: "composite_risk_score", type: "number" },
            { name: "jira_priority", type: "string" },
            { name: "risk_category", type: "string" },
            { name: "ticket_summary", type: "string" },
          ],
        },
      }),

      // 5. Create Jira ticket
      n("5", "hitApi", "Create Jira change request ticket", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"{ticket_summary}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Change: {change_title}\\nType: {change_type}\\nSystems: {systems_affected}\\nDowntime: {estimated_downtime}\\nRisk: {risk_rating} ({composite_risk_score}/100)\\nImpact: {user_impact}\\nRollback: {rollback_adequacy}\\nJustification: {justification}"}]}]},"issuetype":{"name":"Change Request"},"priority":{"name":"{jira_priority}"}}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_issue_id", type: "string" },
            { name: "key", customName: "jira_issue_key", type: "string" },
            { name: "self", customName: "jira_issue_url", type: "string" },
          ],
        },
      }),

      // 6. Requester confirmation email
      emailNode(
        "6",
        "Email requester confirmation",
        1240,
        "{requester_email}",
        "Change Request Submitted: {jira_issue_key} — {change_title}",
        "Hi {requester_name},\n\nYour change request has been submitted and tracked as {jira_issue_key}.\n\nChange: {change_title}\nType: {change_type}\nPriority: {jira_priority}\nRisk Rating: {risk_rating} ({composite_risk_score}/100)\nImplementation Date: {implementation_date}\n\nImpact Assessment:\n- User Impact: {user_impact}\n- Infrastructure Impact: {infrastructure_impact}\n- Rollback Adequacy: {rollback_adequacy}\n\nRecommendation: {approval_recommendation}\n\nYou will be notified once the review is complete.\n\nRegards,\nChange Management",
      ),

      // 7. Approver notification Slack
      slackNode(
        "7",
        "Notify approvers in Slack",
        1480,
        ":memo: *New Change Request: {jira_issue_key}*\n\n*{change_title}*\nRequester: {requester_name} ({department})\nType: {change_type} | Priority: {jira_priority}\nRisk Score: {composite_risk_score}/100 ({risk_rating})\n\nSystems: {systems_affected}\nDowntime: {estimated_downtime}\nRecommendation: {approval_recommendation}\n\nMissing Details: {missing_details}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1292. CLIENT FEEDBACK BOT (8 nodes)
  // ===================================================================

  "Client Feedback Bot": {
    summary:
      "Triggers from a project milestone webhook, extracts client context with AI, generates a personalized survey, delivers it via email, waits 5 days for responses, analyzes sentiment with AI, routes by sentiment score via condition, and aggregates feedback data to Sheets.",
    nodes: [
      trigger("Webhook: Project milestone reached", "webhook_app", {
        source: "project_management",
        event: "milestone.completed",
        howItStarts:
          "Fires when a project milestone is marked complete in your project management tool (Asana, Monday.com, ClickUp), including project name, client info, milestone details, and completion date",
        setup:
          "Configure webhook from your project management tool. Set OpenAI API key for survey generation and sentiment analysis. Provide Google Sheets spreadsheet ID for feedback aggregation.",
        inputFields: [],
      }),

      // 2. AI extract client context
      aiNode(
        "2",
        "Extract client and project context",
        280,
        "You are a project data parser. Extract the project name, client name, client email, milestone name, milestone description, project manager name, project manager email, completion date, and project phase from the raw webhook payload regardless of field naming. Respond JSON: {project_name: string, client_name: string, client_email: string, milestone_name: string, milestone_description: string, pm_name: string, pm_email: string, completion_date: string, project_phase: string}",
        "Raw milestone webhook data: {webhook_payload}",
        [
          { field: "project_name", as: "project_name", type: "string" },
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "milestone_name", as: "milestone_name", type: "string" },
          {
            field: "milestone_description",
            as: "milestone_description",
            type: "string",
          },
          { field: "pm_name", as: "pm_name", type: "string" },
          { field: "pm_email", as: "pm_email", type: "string" },
          { field: "completion_date", as: "completion_date", type: "string" },
          { field: "project_phase", as: "project_phase", type: "string" },
        ],
      ),

      // 3. AI personalized survey generation
      aiNode(
        "3",
        "Generate personalized survey",
        520,
        "You are a client satisfaction survey expert. Generate a personalized feedback survey for a client who just completed a project milestone. The survey should include 3-5 targeted questions relevant to the specific milestone and project phase. Make questions specific and actionable, not generic. Include a mix of scale (1-10) and open-ended questions. Respond JSON: {survey_subject: string, survey_body_html: string, question_count: number, survey_id: string, personalization_notes: string}",
        "Client: {client_name}. Project: {project_name}. Milestone: {milestone_name} — {milestone_description}. Phase: {project_phase}. PM: {pm_name}. Completed: {completion_date}.",
        [
          { field: "survey_subject", as: "survey_subject", type: "string" },
          { field: "survey_body_html", as: "survey_body_html", type: "string" },
          { field: "question_count", as: "question_count", type: "number" },
          { field: "survey_id", as: "survey_id", type: "string" },
          {
            field: "personalization_notes",
            as: "personalization_notes",
            type: "string",
          },
        ],
      ),

      // 4. Multi-channel survey delivery email
      emailNode(
        "4",
        "Send personalized survey email",
        760,
        "{client_email}",
        "{survey_subject}",
        'Hi {client_name},\n\nWe have just completed the "{milestone_name}" milestone for {project_name}, and we would love your feedback.\n\n{survey_body_html}\n\nYour feedback helps us continuously improve our service. This survey takes about 2 minutes.\n\nThank you for your time,\n{pm_name}\nProject Manager',
      ),

      // 5. Delay 5 days for responses
      n("5", "delayAction", "Wait 5 days for survey response", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 6. AI sentiment analysis
      aiNode(
        "6",
        "Analyze feedback sentiment",
        1240,
        "You are a feedback sentiment analyst. Analyze any collected survey responses and the overall client engagement pattern. Based on the project phase, milestone type, and client profile, predict satisfaction level and identify any concerns. Score sentiment from 0 (very negative) to 100 (very positive). Respond JSON: {sentiment_score: number, sentiment_label: string, key_themes: string, concerns_identified: string, satisfaction_prediction: string, follow_up_needed: string, action_items: string}",
        "Project: {project_name}. Client: {client_name}. Milestone: {milestone_name}. Phase: {project_phase}. Survey ID: {survey_id}. Questions sent: {question_count}.",
        [
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "sentiment_label", as: "sentiment_label", type: "string" },
          { field: "key_themes", as: "key_themes", type: "string" },
          {
            field: "concerns_identified",
            as: "concerns_identified",
            type: "string",
          },
          {
            field: "satisfaction_prediction",
            as: "satisfaction_prediction",
            type: "string",
          },
          { field: "follow_up_needed", as: "follow_up_needed", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
        ],
      ),

      // 7. Routing by sentiment condition
      n("7", "condition", "Sentiment positive (score >= 60)?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{sentiment_score}", operator: "gte", value: "60" },
          ],
        },
      }),

      // 8. Feedback aggregation Sheets
      sheetsNode(
        "8",
        "Log feedback data to Sheets",
        1720,
        "ClientFeedback",
        '[["{completion_date}","{project_name}","{client_name}","{milestone_name}","{project_phase}","{sentiment_score}","{sentiment_label}","{key_themes}","{concerns_identified}","{follow_up_needed}","{action_items}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1293. CLIENT FEEDBACK COLLECTION WORKFLOW (8 nodes)
  // ===================================================================

  "Client Feedback Collection Workflow": {
    summary:
      "Runs on a weekly schedule, fetches client list from HubSpot, customizes surveys with runCode, distributes via email, waits 7 days for responses, analyzes themes with AI, generates action items via runCode, and distributes the aggregated report via email.",
    nodes: [
      trigger("Schedule: Weekly feedback collection", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs every Monday morning to initiate weekly client feedback collection cycles, pulling from active client lists and distributing personalized satisfaction surveys",
        setup:
          "Configure HubSpot API token with contacts read access. Set OpenAI API key for analysis. Provide Google Sheets spreadsheet ID. Configure email sending for survey distribution and report delivery.",
        inputFields: [],
      }),

      // 2. Client list HubSpot
      hubspotGetNode(
        "2",
        "Fetch active client list from HubSpot",
        280,
        'crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,company,lifecyclestage&filterGroups=[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"}]}]',
        [
          { name: "results", customName: "client_list", type: "string" },
          { name: "total", customName: "total_clients", type: "number" },
        ],
      ),

      // 3. Survey customization runCode
      n("3", "runCode", "Customize surveys per client segment", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse client list and create segmented survey batches
const clients = JSON.parse(vars.client_list || "[]");
const totalClients = parseInt(vars.total_clients || "0", 10);
const segments = {};
clients.forEach(c => {
  const props = c.properties || {};
  const company = props.company || "Unknown";
  if (!segments[company]) segments[company] = [];
  segments[company].push({
    email: props.email || "",
    name: (props.firstname || "") + " " + (props.lastname || ""),
    company: company
  });
});
const segmentCount = Object.keys(segments).length;
const recipientEmails = clients.map(c => (c.properties || {}).email).filter(Boolean).join(", ");
const companySummary = Object.entries(segments).map(([k, v]) => k + ": " + v.length + " contacts").join("; ");
return {
  recipient_emails: recipientEmails,
  segment_count: segmentCount,
  company_summary: companySummary,
  survey_batch_size: totalClients,
  batch_ready: totalClients > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "recipient_emails", type: "string" },
            { name: "segment_count", type: "number" },
            { name: "company_summary", type: "string" },
            { name: "survey_batch_size", type: "number" },
            { name: "batch_ready", type: "string" },
          ],
        },
      }),

      // 4. Survey distribution email
      emailNode(
        "4",
        "Distribute client satisfaction survey",
        760,
        "{recipient_emails}",
        "We Value Your Feedback — Quick Satisfaction Check-In",
        "Hi there,\n\nAs part of our commitment to delivering exceptional service, we would love to hear how things are going.\n\nPlease take 2 minutes to share your feedback:\n\n1. On a scale of 1-10, how satisfied are you with our service this week?\n2. What is one thing we are doing well?\n3. What is one thing we could improve?\n4. Would you recommend us to a colleague? (Yes/No)\n\nSimply reply to this email with your responses.\n\nThank you for being a valued client!\n\nBest regards,\nClient Success Team",
      ),

      // 5. Delay 7 days for responses
      n("5", "delayAction", "Wait 7 days for survey responses", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 6. AI analysis and theming
      aiNode(
        "6",
        "Analyze feedback themes",
        1240,
        "You are a client feedback analyst. Analyze the survey distribution results and create a comprehensive analysis including response rate estimation, common themes, sentiment trends, and areas requiring attention. Based on segment data, predict overall satisfaction levels. Respond JSON: {estimated_response_rate: string, overall_sentiment: string, positive_themes: string, negative_themes: string, trending_concerns: string, satisfaction_trend: string, segments_at_risk: string, executive_summary: string}",
        "Survey batch sent to {survey_batch_size} clients across {segment_count} companies. Company breakdown: {company_summary}.",
        [
          {
            field: "estimated_response_rate",
            as: "estimated_response_rate",
            type: "string",
          },
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          { field: "positive_themes", as: "positive_themes", type: "string" },
          { field: "negative_themes", as: "negative_themes", type: "string" },
          {
            field: "trending_concerns",
            as: "trending_concerns",
            type: "string",
          },
          {
            field: "satisfaction_trend",
            as: "satisfaction_trend",
            type: "string",
          },
          { field: "segments_at_risk", as: "segments_at_risk", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),

      // 7. Action item generation runCode
      n("7", "runCode", "Generate action items from analysis", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate prioritized action items from feedback analysis
const concerns = vars.trending_concerns || "none identified";
const atRisk = vars.segments_at_risk || "none";
const sentiment = vars.overall_sentiment || "neutral";
const actions = [];
if (atRisk !== "none" && atRisk.length > 5) {
  actions.push("URGENT: Schedule check-in calls with at-risk segments: " + atRisk);
}
if (concerns !== "none identified" && concerns.length > 5) {
  actions.push("REVIEW: Address trending concerns: " + concerns);
}
actions.push("FOLLOW-UP: Send thank-you notes to survey respondents");
actions.push("TRACK: Update client health scores based on sentiment: " + sentiment);
actions.push("PLAN: Incorporate positive themes into case studies: " + (vars.positive_themes || "N/A"));
return {
  action_items_list: actions.join("\\n"),
  action_count: actions.length,
  priority_action: actions[0] || "No urgent actions",
  report_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "action_items_list", type: "string" },
            { name: "action_count", type: "number" },
            { name: "priority_action", type: "string" },
            { name: "report_date", type: "string" },
          ],
        },
      }),

      // 8. Report distribution email
      emailNode(
        "8",
        "Email weekly feedback report",
        1720,
        "{cs_team_email}",
        "Weekly Client Feedback Report — {report_date}",
        "Client Feedback Weekly Report\n\nSurvey Distribution:\n- Clients Surveyed: {survey_batch_size}\n- Companies: {segment_count}\n- Estimated Response Rate: {estimated_response_rate}\n\nSentiment Analysis:\n- Overall Sentiment: {overall_sentiment}\n- Satisfaction Trend: {satisfaction_trend}\n\nPositive Themes: {positive_themes}\nNegative Themes: {negative_themes}\nTrending Concerns: {trending_concerns}\nSegments at Risk: {segments_at_risk}\n\nExecutive Summary:\n{executive_summary}\n\nAction Items ({action_count}):\n{action_items_list}\n\nPriority Action: {priority_action}\n\nBest,\nFeedback Automation System",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1294. CLIENT PROJECT UPDATE CAMPAIGN (7 nodes)
  // ===================================================================

  "Client Project Update Campaign": {
    summary:
      "Runs weekly on a schedule, aggregates project data from Asana, tracks time and budget via runCode, generates AI status summaries, emails clients with project updates, checks project health via condition, and logs update delivery to Sheets.",
    nodes: [
      trigger("Schedule: Weekly project updates", "schedule", {
        schedule: "Every Friday at 3:00 PM",
        howItStarts:
          "Runs every Friday afternoon to compile weekly project status updates for all active client projects, pulling task completion data, time tracking, and budget utilization",
        setup:
          "Configure Asana personal access token with project read access. Set OpenAI API key for status summary generation. Configure email sending for client updates. Provide Google Sheets spreadsheet ID for delivery tracking.",
        inputFields: [],
      }),

      // 2. Project data aggregation Asana API
      n("2", "hitApi", "Fetch active projects from Asana", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/projects?workspace={asana_workspace_gid}&opt_fields=name,owner.email,owner.name,current_status,due_on,completed,num_tasks,num_incomplete_tasks,custom_fields&archived=false",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "projects_data", type: "string" },
          ],
        },
      }),

      // 3. Time and budget tracking runCode
      n("3", "runCode", "Calculate project health metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Asana projects and calculate health metrics
const projects = JSON.parse(vars.projects_data || "[]");
const summaries = [];
let totalTasks = 0;
let completedTasks = 0;
let atRiskCount = 0;
let onTrackCount = 0;
projects.forEach(p => {
  const total = parseInt(p.num_tasks || "0", 10);
  const incomplete = parseInt(p.num_incomplete_tasks || "0", 10);
  const done = total - incomplete;
  totalTasks += total;
  completedTasks += done;
  const completionPct = total > 0 ? Math.round((done / total) * 100) : 0;
  const status = (p.current_status || {}).text || "no status";
  const dueDate = p.due_on || "no due date";
  const isOverdue = dueDate !== "no due date" && new Date(dueDate) < new Date();
  const health = isOverdue ? "at-risk" : completionPct >= 70 ? "on-track" : completionPct >= 40 ? "needs-attention" : "at-risk";
  if (health === "at-risk") atRiskCount++;
  else onTrackCount++;
  summaries.push(p.name + ": " + completionPct + "% complete (" + done + "/" + total + " tasks) — " + health);
});
const overallCompletion = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
return {
  project_summaries: summaries.join("\\n"),
  project_count: projects.length,
  total_tasks: totalTasks,
  completed_tasks: completedTasks,
  overall_completion_pct: overallCompletion,
  at_risk_count: atRiskCount,
  on_track_count: onTrackCount,
  portfolio_health: atRiskCount === 0 ? "healthy" : atRiskCount <= 2 ? "attention_needed" : "critical"
};`,
          outputVariables: [
            { name: "project_summaries", type: "string" },
            { name: "project_count", type: "number" },
            { name: "total_tasks", type: "number" },
            { name: "completed_tasks", type: "number" },
            { name: "overall_completion_pct", type: "number" },
            { name: "at_risk_count", type: "number" },
            { name: "on_track_count", type: "number" },
            { name: "portfolio_health", type: "string" },
          ],
        },
      }),

      // 4. AI status summary
      aiNode(
        "4",
        "Generate project status summary",
        760,
        "You are a project status report writer. Create a professional, client-friendly weekly status update from the project data. Highlight key accomplishments, upcoming milestones, any blockers or risks, and next week priorities. Keep the tone professional but warm. Respond JSON: {status_report: string, key_accomplishments: string, upcoming_milestones: string, blockers: string, next_week_priorities: string, client_talking_points: string}",
        "Portfolio: {project_count} active projects. Overall completion: {overall_completion_pct}%. At risk: {at_risk_count}. On track: {on_track_count}.\n\nProject Details:\n{project_summaries}",
        [
          { field: "status_report", as: "status_report", type: "string" },
          {
            field: "key_accomplishments",
            as: "key_accomplishments",
            type: "string",
          },
          {
            field: "upcoming_milestones",
            as: "upcoming_milestones",
            type: "string",
          },
          { field: "blockers", as: "blockers", type: "string" },
          {
            field: "next_week_priorities",
            as: "next_week_priorities",
            type: "string",
          },
          {
            field: "client_talking_points",
            as: "client_talking_points",
            type: "string",
          },
        ],
      ),

      // 5. Client update email
      emailNode(
        "5",
        "Send weekly project update email",
        1000,
        "{client_email}",
        "Weekly Project Update — {overall_completion_pct}% Complete ({project_count} Projects)",
        "Weekly Project Status Update\n\n{status_report}\n\nKey Accomplishments:\n{key_accomplishments}\n\nUpcoming Milestones:\n{upcoming_milestones}\n\nBlockers/Risks:\n{blockers}\n\nNext Week Priorities:\n{next_week_priorities}\n\nPlease let us know if you have any questions or concerns.\n\nBest regards,\nProject Management Team",
      ),

      // 6. Internal project health alert condition
      n("6", "condition", "Portfolio health critical?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{portfolio_health}",
              operator: "equals",
              value: "critical",
            },
          ],
        },
      }),

      // 7. Update delivery tracking Sheets
      sheetsNode(
        "7",
        "Log update delivery to Sheets",
        1480,
        "ProjectUpdates",
        '[["{project_count}","{overall_completion_pct}","{at_risk_count}","{on_track_count}","{portfolio_health}","{key_accomplishments}","{blockers}","{next_week_priorities}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1295. COMMUNITY PROGRAM REGISTRATION BOT (8 nodes)
  // ===================================================================

  "Community Program Registration Bot": {
    summary:
      "Triggers from a registration inquiry webhook, uses AI for conversational intake, checks program availability via API, validates eligibility via condition, creates a HubSpot contact for the registrant, emails confirmation, checks payment status via condition, and manages capacity via Slack.",
    nodes: [
      trigger("Webhook: Registration inquiry received", "webhook_app", {
        source: "community_platform",
        event: "registration.inquiry",
        howItStarts:
          "Fires when a prospective community member submits a registration inquiry through a community portal, including their name, email, program interest, background, and any qualifying information",
        setup:
          "Configure webhook from your community platform or registration form. Set OpenAI API key for conversational intake. Provide HubSpot API token for contact creation. Configure Slack webhook for capacity management alerts.",
        inputFields: [],
      }),

      // 2. AI conversational intake
      aiNode(
        "2",
        "Process registration inquiry",
        280,
        "You are a community program registration assistant. Parse the registration inquiry and extract the applicant name, email, phone, program of interest, background/qualifications, experience level, referral source, and any special requirements or accommodations. Also assess eligibility based on stated qualifications. Respond JSON: {applicant_name: string, applicant_email: string, applicant_phone: string, program_interest: string, background: string, experience_level: string, referral_source: string, special_requirements: string, eligibility_assessment: string, is_eligible: string}",
        "Raw registration inquiry: {webhook_payload}",
        [
          { field: "applicant_name", as: "applicant_name", type: "string" },
          { field: "applicant_email", as: "applicant_email", type: "string" },
          { field: "applicant_phone", as: "applicant_phone", type: "string" },
          { field: "program_interest", as: "program_interest", type: "string" },
          { field: "background", as: "background", type: "string" },
          { field: "experience_level", as: "experience_level", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
          {
            field: "eligibility_assessment",
            as: "eligibility_assessment",
            type: "string",
          },
          { field: "is_eligible", as: "is_eligible", type: "string" },
        ],
      ),

      // 3. Program availability check API
      n("3", "hitApi", "Check program availability", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ProgramCapacity!A2:F20",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values",
              customName: "program_capacity_data",
              type: "string",
            },
            { name: "range", customName: "capacity_range", type: "string" },
          ],
        },
      }),

      // 4. Eligibility validation condition
      n("4", "condition", "Applicant eligible?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_eligible}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 5. Registration creation HubSpot (true path)
      hubspotPostNode(
        "5",
        "Create HubSpot contact for registrant",
        1000,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{applicant_email}","firstname":"{applicant_name}","phone":"{applicant_phone}","lifecyclestage":"lead","hs_lead_status":"NEW","company":"{program_interest}","jobtitle":"{experience_level}","message":"{background}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "created_email",
            type: "string",
          },
        ],
      ),

      // 6. Confirmation email
      emailNode(
        "6",
        "Send registration confirmation",
        1240,
        "{applicant_email}",
        "Welcome! Your Registration for {program_interest} is Confirmed",
        "Hi {applicant_name},\n\nThank you for registering for our {program_interest} program!\n\nRegistration Details:\n- Program: {program_interest}\n- Experience Level: {experience_level}\n- Special Requirements: {special_requirements}\n\nEligibility Status: {eligibility_assessment}\n\nNext Steps:\n1. You will receive program materials within 48 hours\n2. Complete the onboarding checklist\n3. Join our community channels\n\nIf you have questions, reply to this email.\n\nWelcome aboard!\nCommunity Team",
      ),

      // 7. Payment processing condition
      n("7", "condition", "Program requires payment?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{program_interest}",
              operator: "contains",
              value: "premium",
            },
          ],
        },
      }),

      // 8. Capacity management Slack
      slackNode(
        "8",
        "Update capacity in Slack",
        1720,
        ":busts_in_silhouette: *New Community Registration*\n\nApplicant: {applicant_name} ({applicant_email})\nProgram: {program_interest}\nExperience: {experience_level}\nReferral: {referral_source}\nEligibility: {eligibility_assessment}\nHubSpot ID: {hubspot_contact_id}\n\nPlease update program capacity tracking.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1296. COMPANY NEWS DIGEST (7 nodes)
  // ===================================================================

  "Company News Digest": {
    summary:
      "Runs daily on a schedule, aggregates external news via API, fetches internal updates from Slack, summarizes and prioritizes everything with AI, compiles the digest with runCode, distributes via email, and posts to Slack for company-wide visibility.",
    nodes: [
      trigger("Schedule: Daily news digest", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs every morning to aggregate industry news, company mentions, and internal updates into a single daily digest for the team",
        setup:
          "Configure NewsAPI key for external news aggregation. Set Slack API token for internal updates retrieval. Set OpenAI API key for summarization. Configure email distribution list and Slack webhook for digest posting.",
        inputFields: [],
      }),

      // 2. News aggregation API
      n("2", "hitApi", "Fetch industry news from NewsAPI", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://newsapi.org/v2/everything?q={company_name}+OR+{industry_keywords}&sortBy=publishedAt&pageSize=20&apiKey={newsapi_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "articles", customName: "news_articles", type: "string" },
            {
              name: "totalResults",
              customName: "total_news_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Internal updates fetch Slack API
      n("3", "hitApi", "Fetch internal updates from Slack", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.history?channel={announcements_channel_id}&limit=15",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "messages", customName: "slack_messages", type: "string" },
            { name: "ok", customName: "slack_fetch_ok", type: "boolean" },
          ],
        },
      }),

      // 4. AI summarization and prioritization
      aiNode(
        "4",
        "Summarize and prioritize news",
        760,
        "You are a company news digest curator. From external news articles and internal Slack messages, create a prioritized daily digest. Categorize items as: Company Mentions, Industry Trends, Competitor News, Internal Announcements, and Action Required. Summarize each item in 1-2 sentences. Rank by relevance and impact. Respond JSON: {digest_headline: string, company_mentions: string, industry_trends: string, competitor_news: string, internal_updates: string, action_required: string, total_items: number, priority_items_count: number}",
        "External news ({total_news_count} articles): {news_articles}\n\nInternal Slack messages: {slack_messages}",
        [
          { field: "digest_headline", as: "digest_headline", type: "string" },
          { field: "company_mentions", as: "company_mentions", type: "string" },
          { field: "industry_trends", as: "industry_trends", type: "string" },
          { field: "competitor_news", as: "competitor_news", type: "string" },
          { field: "internal_updates", as: "internal_updates", type: "string" },
          { field: "action_required", as: "action_required", type: "string" },
          { field: "total_items", as: "total_items", type: "number" },
          {
            field: "priority_items_count",
            as: "priority_items_count",
            type: "number",
          },
        ],
      ),

      // 5. Digest compilation runCode
      n("5", "runCode", "Compile final digest format", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile the final digest with formatting and metadata
const today = new Date().toISOString().split("T")[0];
const sections = [];
if (vars.company_mentions && vars.company_mentions !== "none") sections.push("COMPANY MENTIONS:\\n" + vars.company_mentions);
if (vars.industry_trends && vars.industry_trends !== "none") sections.push("INDUSTRY TRENDS:\\n" + vars.industry_trends);
if (vars.competitor_news && vars.competitor_news !== "none") sections.push("COMPETITOR NEWS:\\n" + vars.competitor_news);
if (vars.internal_updates && vars.internal_updates !== "none") sections.push("INTERNAL UPDATES:\\n" + vars.internal_updates);
if (vars.action_required && vars.action_required !== "none") sections.push("ACTION REQUIRED:\\n" + vars.action_required);
const fullDigest = sections.join("\\n\\n---\\n\\n");
return {
  digest_date: today,
  full_digest: fullDigest,
  section_count: sections.length,
  digest_subject: vars.digest_headline + " — " + today,
  has_action_items: (vars.action_required && vars.action_required !== "none") ? "yes" : "no"
};`,
          outputVariables: [
            { name: "digest_date", type: "string" },
            { name: "full_digest", type: "string" },
            { name: "section_count", type: "number" },
            { name: "digest_subject", type: "string" },
            { name: "has_action_items", type: "string" },
          ],
        },
      }),

      // 6. Email distribution
      emailNode(
        "6",
        "Email daily digest to team",
        1240,
        "{team_distribution_email}",
        "{digest_subject}",
        "Daily News Digest — {digest_date}\n\n{digest_headline}\n\n{total_items} items curated ({priority_items_count} priority)\n\n{full_digest}\n\n---\nThis digest is automatically curated each morning. Reply with feedback or suggestions.\n\nBest,\nNews Digest Bot",
      ),

      // 7. Slack digest posting
      slackNode(
        "7",
        "Post digest to Slack",
        1480,
        ":newspaper: *Daily News Digest — {digest_date}*\n\n*{digest_headline}*\n{total_items} items | {priority_items_count} priority\n\n{full_digest}\n\nAction Items: {has_action_items}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1297. COMPANY OKR TRACKING BOT (8 nodes)
  // ===================================================================

  "Company OKR Tracking Bot": {
    summary:
      "Runs weekly on schedule, fetches OKR data from Google Sheets, requests owner updates via Slack, waits 1 day for responses, collects responses via runCode, analyzes progress with AI, updates a status dashboard in Sheets, and posts a leadership summary to Slack.",
    nodes: [
      trigger("Schedule: Weekly OKR tracking", "schedule", {
        schedule: "Every Monday at 10:00 AM",
        howItStarts:
          "Runs every Monday morning to initiate the weekly OKR check-in cycle, pulling current objective and key result data and requesting progress updates from owners",
        setup:
          "Configure Google Sheets spreadsheet ID with OKR tracking tab. Set Slack bot token for sending update requests and posting summaries. Set OpenAI API key for progress analysis.",
        inputFields: [],
      }),

      // 2. OKR data fetch Sheets
      n("2", "hitApi", "Fetch OKR data from Google Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/OKRs!A2:J50",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "okr_data", type: "string" },
            { name: "range", customName: "okr_range", type: "string" },
          ],
        },
      }),

      // 3. Owner update request Slack
      slackNode(
        "3",
        "Request OKR updates from owners",
        520,
        ":dart: *Weekly OKR Check-In*\n\nHi team! It's time for our weekly OKR progress update.\n\nPlease reply in the thread with your updates using this format:\n- Objective: [name]\n- Current progress: [%]\n- Key wins this week:\n- Blockers:\n- Confidence level (High/Medium/Low):\n\nDeadline: Tomorrow EOD. Thank you!",
      ),

      // 4. Delay 1 day for responses
      n("4", "delayAction", "Wait 1 day for owner responses", 760, {
        delayDetails: { duration: 1, unit: "days" },
      }),

      // 5. Response collection runCode
      n("5", "runCode", "Process OKR update responses", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse OKR data and compute aggregate metrics
const okrRows = JSON.parse(vars.okr_data || "[]");
let totalObjectives = 0;
let totalProgress = 0;
let onTrack = 0;
let atRisk = 0;
let behind = 0;
const objectiveSummaries = [];
okrRows.forEach(row => {
  if (row && row.length >= 4) {
    totalObjectives++;
    const objective = row[0] || "Unnamed";
    const owner = row[1] || "Unassigned";
    const target = parseInt(row[2] || "100", 10);
    const current = parseInt(row[3] || "0", 10);
    const pct = target > 0 ? Math.round((current / target) * 100) : 0;
    totalProgress += pct;
    const status = pct >= 70 ? "on-track" : pct >= 40 ? "at-risk" : "behind";
    if (status === "on-track") onTrack++;
    else if (status === "at-risk") atRisk++;
    else behind++;
    objectiveSummaries.push(objective + " (" + owner + "): " + pct + "% — " + status);
  }
});
const avgProgress = totalObjectives > 0 ? Math.round(totalProgress / totalObjectives) : 0;
return {
  objective_count: totalObjectives,
  avg_progress: avgProgress,
  on_track_count: onTrack,
  at_risk_count: atRisk,
  behind_count: behind,
  objective_summaries: objectiveSummaries.join("\\n"),
  overall_health: behind > onTrack ? "needs_attention" : "healthy"
};`,
          outputVariables: [
            { name: "objective_count", type: "number" },
            { name: "avg_progress", type: "number" },
            { name: "on_track_count", type: "number" },
            { name: "at_risk_count", type: "number" },
            { name: "behind_count", type: "number" },
            { name: "objective_summaries", type: "string" },
            { name: "overall_health", type: "string" },
          ],
        },
      }),

      // 6. AI progress analysis
      aiNode(
        "6",
        "Analyze OKR progress and trends",
        1240,
        "You are an OKR analyst. Analyze the weekly OKR progress data and provide insights on trends, identify at-risk objectives requiring intervention, highlight wins, and recommend focus areas for the coming week. Provide an executive-level summary suitable for leadership review. Respond JSON: {executive_summary: string, wins_this_week: string, at_risk_objectives: string, recommended_focus: string, trend_analysis: string, confidence_assessment: string}",
        "OKR Status: {objective_count} objectives, avg progress {avg_progress}%. On track: {on_track_count}, At risk: {at_risk_count}, Behind: {behind_count}.\n\nDetails:\n{objective_summaries}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "wins_this_week", as: "wins_this_week", type: "string" },
          {
            field: "at_risk_objectives",
            as: "at_risk_objectives",
            type: "string",
          },
          {
            field: "recommended_focus",
            as: "recommended_focus",
            type: "string",
          },
          { field: "trend_analysis", as: "trend_analysis", type: "string" },
          {
            field: "confidence_assessment",
            as: "confidence_assessment",
            type: "string",
          },
        ],
      ),

      // 7. Status dashboard update Sheets
      sheetsNode(
        "7",
        "Update OKR dashboard in Sheets",
        1480,
        "OKRDashboard",
        '[["{objective_count}","{avg_progress}","{on_track_count}","{at_risk_count}","{behind_count}","{overall_health}","{wins_this_week}","{at_risk_objectives}","{recommended_focus}"]]',
      ),

      // 8. Leadership summary Slack
      slackNode(
        "8",
        "Post leadership OKR summary to Slack",
        1720,
        ":chart_with_upwards_trend: *Weekly OKR Report*\n\n*{executive_summary}*\n\n:white_check_mark: On Track: {on_track_count} | :warning: At Risk: {at_risk_count} | :red_circle: Behind: {behind_count}\nAverage Progress: {avg_progress}% | Health: {overall_health}\n\nWins: {wins_this_week}\nAt Risk: {at_risk_objectives}\nFocus: {recommended_focus}\n\nTrend: {trend_analysis}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1298. COMPLIANCE DOCUMENT CHECKER (8 nodes)
  // ===================================================================

  "Compliance Document Checker": {
    summary:
      "Triggers from a document upload webhook, extracts document text with AI, performs compliance analysis with AI, cross-references regulations via runCode, classifies severity via condition, emails the compliance report, notifies the team in Slack, and logs the audit trail to Sheets.",
    nodes: [
      trigger("Webhook: Document uploaded for review", "webhook_app", {
        source: "document_management",
        event: "document.uploaded",
        howItStarts:
          "Fires when a document is uploaded to a document management system (Google Drive, SharePoint, Dropbox) for compliance review, including the document text, metadata, uploader info, and document type",
        setup:
          "Configure webhook from your document management system. Set OpenAI API key for document analysis. Configure email for compliance report delivery. Set Slack webhook for team notifications. Provide Google Sheets spreadsheet ID for audit trail.",
        inputFields: [],
      }),

      // 2. AI extract document text
      aiNode(
        "2",
        "Extract document metadata and content",
        280,
        "You are a document parser. Extract the document title, document type (policy, contract, report, procedure), uploader name, uploader email, upload date, department, document content summary, and key sections from the raw webhook payload regardless of field naming. Respond JSON: {doc_title: string, doc_type: string, uploader_name: string, uploader_email: string, upload_date: string, department: string, content_summary: string, key_sections: string}",
        "Raw document upload webhook data: {webhook_payload}",
        [
          { field: "doc_title", as: "doc_title", type: "string" },
          { field: "doc_type", as: "doc_type", type: "string" },
          { field: "uploader_name", as: "uploader_name", type: "string" },
          { field: "uploader_email", as: "uploader_email", type: "string" },
          { field: "upload_date", as: "upload_date", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "content_summary", as: "content_summary", type: "string" },
          { field: "key_sections", as: "key_sections", type: "string" },
        ],
      ),

      // 3. AI compliance analysis
      aiNode(
        "3",
        "Perform compliance analysis",
        520,
        "You are a regulatory compliance analyst. Review the document content for compliance with GDPR, SOC2, HIPAA, and industry-specific regulations based on the document type and department. Identify non-compliant sections, missing required elements, and potential regulatory risks. Score compliance 0-100. Respond JSON: {compliance_score: number, compliance_status: string, non_compliant_sections: string, missing_elements: string, regulatory_risks: string, applicable_regulations: string, remediation_steps: string, severity_level: string}",
        "Document: {doc_title}. Type: {doc_type}. Department: {department}.\n\nContent Summary: {content_summary}\n\nKey Sections: {key_sections}",
        [
          { field: "compliance_score", as: "compliance_score", type: "number" },
          {
            field: "compliance_status",
            as: "compliance_status",
            type: "string",
          },
          {
            field: "non_compliant_sections",
            as: "non_compliant_sections",
            type: "string",
          },
          { field: "missing_elements", as: "missing_elements", type: "string" },
          { field: "regulatory_risks", as: "regulatory_risks", type: "string" },
          {
            field: "applicable_regulations",
            as: "applicable_regulations",
            type: "string",
          },
          {
            field: "remediation_steps",
            as: "remediation_steps",
            type: "string",
          },
          { field: "severity_level", as: "severity_level", type: "string" },
        ],
      ),

      // 4. Regulatory cross-reference runCode
      n("4", "runCode", "Cross-reference regulatory requirements", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Cross-reference compliance findings with regulatory framework requirements
const score = parseInt(vars.compliance_score || "0", 10);
const docType = (vars.doc_type || "").toLowerCase();
const regulations = (vars.applicable_regulations || "").toLowerCase();
const requiredChecks = [];
if (regulations.includes("gdpr")) requiredChecks.push("Data processing agreement", "Privacy impact assessment", "Data retention policy", "Right to erasure provisions");
if (regulations.includes("soc2")) requiredChecks.push("Access control documentation", "Encryption standards", "Incident response plan", "Vendor management policy");
if (regulations.includes("hipaa")) requiredChecks.push("PHI handling procedures", "Business associate agreement", "Breach notification plan", "Minimum necessary standard");
const checkCount = requiredChecks.length;
const passThreshold = score >= 70;
const reviewRequired = score < 85 ? "yes" : "no";
const escalationNeeded = score < 50 ? "yes" : "no";
return {
  required_checks: requiredChecks.join(", ") || "General compliance checks",
  check_count: checkCount,
  passes_threshold: passThreshold ? "yes" : "no",
  review_required: reviewRequired,
  escalation_needed: escalationNeeded,
  framework_notes: "Document checked against " + checkCount + " regulatory requirements. Score: " + score + "/100."
};`,
          outputVariables: [
            { name: "required_checks", type: "string" },
            { name: "check_count", type: "number" },
            { name: "passes_threshold", type: "string" },
            { name: "review_required", type: "string" },
            { name: "escalation_needed", type: "string" },
            { name: "framework_notes", type: "string" },
          ],
        },
      }),

      // 5. Issue severity classification condition
      n("5", "condition", "Compliance score passes threshold?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{passes_threshold}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 6. Compliance report email
      emailNode(
        "6",
        "Email compliance report",
        1240,
        "{uploader_email}",
        "Compliance Review: {doc_title} — Score {compliance_score}/100 ({compliance_status})",
        "Compliance Review Report\n\nDocument: {doc_title}\nType: {doc_type}\nDepartment: {department}\nUpload Date: {upload_date}\n\nCompliance Score: {compliance_score}/100\nStatus: {compliance_status}\nSeverity: {severity_level}\n\nApplicable Regulations: {applicable_regulations}\nRequired Checks: {required_checks}\n\nNon-Compliant Sections:\n{non_compliant_sections}\n\nMissing Elements:\n{missing_elements}\n\nRegulatory Risks:\n{regulatory_risks}\n\nRemediation Steps:\n{remediation_steps}\n\n{framework_notes}\n\nPlease address the identified issues and re-submit for review.\n\nRegards,\nCompliance Review System",
      ),

      // 7. Team notification Slack
      slackNode(
        "7",
        "Notify compliance team in Slack",
        1480,
        ":shield: *Compliance Review Complete: {doc_title}*\n\nScore: {compliance_score}/100 ({compliance_status})\nSeverity: {severity_level}\nDepartment: {department}\nUploader: {uploader_name}\n\nEscalation Needed: {escalation_needed}\nManual Review Required: {review_required}\n\nKey Issues: {non_compliant_sections}",
      ),

      // 8. Audit trail logging Sheets
      sheetsNode(
        "8",
        "Log audit trail to Sheets",
        1720,
        "ComplianceAudit",
        '[["{upload_date}","{doc_title}","{doc_type}","{department}","{uploader_name}","{compliance_score}","{compliance_status}","{severity_level}","{applicable_regulations}","{passes_threshold}","{escalation_needed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1299. CONTACT INFORMATION UPDATER (7 nodes)
  // ===================================================================

  "Contact Information Updater": {
    summary:
      "Triggers from a contact update webhook, extracts contact data with AI, validates data integrity via runCode, detects conflicts via condition, updates the CRM record in HubSpot, syncs to external systems via API, and logs the update confirmation to Sheets.",
    nodes: [
      trigger("Webhook: Contact update event", "webhook_app", {
        source: "crm_platform",
        event: "contact.updated",
        howItStarts:
          "Fires when a contact update is received from any source — CRM form, email parsing, third-party enrichment service, or manual entry — including the new contact details and source information",
        setup:
          "Configure webhook from your CRM or data enrichment service. Set OpenAI API key for data extraction. Provide HubSpot API token for CRM updates. Configure external system API endpoint for cross-system sync. Set Google Sheets spreadsheet ID for update logging.",
        inputFields: [],
      }),

      // 2. AI extract contact data
      aiNode(
        "2",
        "Extract contact update details",
        280,
        "You are a contact data parser. Extract the contact email, first name, last name, phone, company, job title, address, update source, previous values (if provided), and new values from the raw webhook payload regardless of field naming. Respond JSON: {contact_email: string, first_name: string, last_name: string, phone: string, company: string, job_title: string, address: string, update_source: string, previous_values: string, new_values: string}",
        "Raw contact update webhook data: {webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "phone", as: "phone", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "address", as: "address", type: "string" },
          { field: "update_source", as: "update_source", type: "string" },
          { field: "previous_values", as: "previous_values", type: "string" },
          { field: "new_values", as: "new_values", type: "string" },
        ],
      ),

      // 3. Data validation runCode
      n("3", "runCode", "Validate contact data integrity", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate contact data fields for completeness and format
const email = vars.contact_email || "";
const phone = vars.phone || "";
const firstName = vars.first_name || "";
const lastName = vars.last_name || "";
const emailValid = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(email);
const phoneClean = phone.replace(/[^0-9+]/g, "");
const phoneValid = phoneClean.length >= 10;
const nameValid = firstName.length >= 1 && lastName.length >= 1;
const issues = [];
if (!emailValid) issues.push("Invalid email format");
if (!phoneValid && phone.length > 0) issues.push("Invalid phone format");
if (!nameValid) issues.push("Missing name fields");
const hasPrevious = (vars.previous_values || "").trim().length > 5;
const hasConflict = hasPrevious ? "possible" : "no";
const validationScore = (emailValid ? 30 : 0) + (phoneValid ? 25 : 0) + (nameValid ? 25 : 0) + 20;
return {
  is_valid: issues.length === 0 ? "yes" : "no",
  validation_issues: issues.join("; ") || "none",
  validation_score: validationScore,
  phone_cleaned: phoneClean,
  has_conflict: hasConflict,
  update_safe: issues.length === 0 && hasConflict === "no" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "is_valid", type: "string" },
            { name: "validation_issues", type: "string" },
            { name: "validation_score", type: "number" },
            { name: "phone_cleaned", type: "string" },
            { name: "has_conflict", type: "string" },
            { name: "update_safe", type: "string" },
          ],
        },
      }),

      // 4. Conflict detection condition
      n("4", "condition", "Update safe (no conflicts)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{update_safe}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 5. CRM update execution HubSpot (true path)
      n("5", "hitApi", "Update HubSpot contact record", 1000, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{contact_email}","firstname":"{first_name}","lastname":"{last_name}","phone":"{phone_cleaned}","company":"{company}","jobtitle":"{job_title}","address":"{address}"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_contact_id", type: "string" },
            {
              name: "properties.email",
              customName: "confirmed_email",
              type: "string",
            },
          ],
        },
      }),

      // 6. Cross-system sync API
      n("6", "hitApi", "Sync update to external systems", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/identify",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{contact_email}" },
            {
              key: "traits",
              value:
                '{"email":"{contact_email}","firstName":"{first_name}","lastName":"{last_name}","phone":"{phone_cleaned}","company":"{company}","title":"{job_title}","address":"{address}"}',
            },
          ],
          variables: [
            { name: "success", customName: "sync_success", type: "boolean" },
          ],
        },
      }),

      // 7. Update confirmation Sheets
      sheetsNode(
        "7",
        "Log contact update to Sheets",
        1480,
        "ContactUpdates",
        '[["{contact_email}","{first_name}","{last_name}","{company}","{update_source}","{validation_score}","{has_conflict}","{updated_contact_id}","{sync_success}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1300. CONTRACT CLAUSE LIBRARY BOT (7 nodes)
  // ===================================================================

  "Contract Clause Library Bot": {
    summary:
      "Triggers from a clause query webhook, extracts query intent with AI, searches the clause library via runCode, recommends relevant clauses with AI, customizes selected clauses via runCode, checks legal compliance via condition, and delivers the final clause to Slack.",
    nodes: [
      trigger("Webhook: Clause query received", "webhook_app", {
        source: "legal_platform",
        event: "clause.query",
        howItStarts:
          "Fires when a team member queries the contract clause library via Slack command, internal portal, or API, including the clause type needed, contract context, jurisdiction, and any specific requirements",
        setup:
          "Configure webhook from your Slack slash command or internal portal. Set OpenAI API key for intent extraction and clause recommendation. Configure Google Sheets spreadsheet ID with clause library tab. Set Slack webhook for clause delivery.",
        inputFields: [],
      }),

      // 2. AI extract query intent
      aiNode(
        "2",
        "Parse clause query intent",
        280,
        "You are a legal query parser. Extract the requested clause type (e.g., indemnification, limitation of liability, confidentiality, termination, force majeure), contract context (deal type, counterparty, industry), jurisdiction, requester name, requester email, urgency level, and any specific customization requirements from the raw webhook payload. Respond JSON: {clause_type: string, contract_context: string, jurisdiction: string, requester_name: string, requester_email: string, urgency: string, customization_requirements: string}",
        "Raw clause query data: {webhook_payload}",
        [
          { field: "clause_type", as: "clause_type", type: "string" },
          { field: "contract_context", as: "contract_context", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "customization_requirements",
            as: "customization_requirements",
            type: "string",
          },
        ],
      ),

      // 3. Clause search runCode
      n("3", "runCode", "Search clause library database", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Search and match clauses from the clause library
const clauseType = (vars.clause_type || "").toLowerCase();
const jurisdiction = (vars.jurisdiction || "").toLowerCase();
const clauseLibrary = {
  "indemnification": { id: "IND-001", text: "Each party shall indemnify, defend, and hold harmless the other party from and against all claims, damages, losses, costs, and expenses arising from...", risk_level: "high", common_jurisdictions: ["us", "uk", "eu"] },
  "limitation of liability": { id: "LOL-001", text: "In no event shall either party be liable to the other for any indirect, incidental, special, consequential, or punitive damages...", risk_level: "high", common_jurisdictions: ["us", "uk", "eu"] },
  "confidentiality": { id: "CON-001", text: "Each party agrees to maintain in strict confidence all Confidential Information received from the other party...", risk_level: "medium", common_jurisdictions: ["us", "uk", "eu", "apac"] },
  "termination": { id: "TER-001", text: "Either party may terminate this Agreement upon thirty (30) days prior written notice...", risk_level: "medium", common_jurisdictions: ["us", "uk", "eu"] },
  "force majeure": { id: "FM-001", text: "Neither party shall be liable for any failure or delay in performing its obligations under this Agreement if such failure or delay results from...", risk_level: "low", common_jurisdictions: ["us", "uk", "eu", "apac"] }
};
const match = Object.entries(clauseLibrary).find(([k]) => clauseType.includes(k));
const clause = match ? match[1] : { id: "GEN-001", text: "Standard clause for " + clauseType + " — please customize for your specific needs.", risk_level: "medium", common_jurisdictions: ["us"] };
const jurisdictionMatch = clause.common_jurisdictions.includes(jurisdiction.substring(0, 2)) ? "yes" : "review_needed";
return {
  clause_id: clause.id,
  clause_text: clause.text,
  clause_risk_level: clause.risk_level,
  jurisdiction_match: jurisdictionMatch,
  search_match_found: match ? "yes" : "partial",
  available_variations: "3"
};`,
          outputVariables: [
            { name: "clause_id", type: "string" },
            { name: "clause_text", type: "string" },
            { name: "clause_risk_level", type: "string" },
            { name: "jurisdiction_match", type: "string" },
            { name: "search_match_found", type: "string" },
            { name: "available_variations", type: "string" },
          ],
        },
      }),

      // 4. AI clause recommendation
      aiNode(
        "4",
        "Generate clause recommendation",
        760,
        "You are a legal clause advisor. Based on the matched clause from the library, the contract context, jurisdiction, and customization requirements, provide a recommended version of the clause with appropriate modifications. Explain why you made specific changes and flag any risks. Respond JSON: {recommended_clause: string, modifications_made: string, risk_warnings: string, alternative_suggestions: string, compliance_notes: string, is_legally_sound: string}",
        "Clause Type: {clause_type}. Base Clause ({clause_id}): {clause_text}. Context: {contract_context}. Jurisdiction: {jurisdiction}. Customization: {customization_requirements}. Risk Level: {clause_risk_level}. Jurisdiction Match: {jurisdiction_match}.",
        [
          {
            field: "recommended_clause",
            as: "recommended_clause",
            type: "string",
          },
          {
            field: "modifications_made",
            as: "modifications_made",
            type: "string",
          },
          { field: "risk_warnings", as: "risk_warnings", type: "string" },
          {
            field: "alternative_suggestions",
            as: "alternative_suggestions",
            type: "string",
          },
          { field: "compliance_notes", as: "compliance_notes", type: "string" },
          { field: "is_legally_sound", as: "is_legally_sound", type: "string" },
        ],
      ),

      // 5. Clause customization runCode
      n("5", "runCode", "Format clause for delivery", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the recommended clause with metadata for delivery
const clauseType = vars.clause_type || "General";
const clauseId = vars.clause_id || "GEN-001";
const jurisdiction = vars.jurisdiction || "Not specified";
const riskLevel = vars.clause_risk_level || "medium";
const formattedClause = "--- CLAUSE: " + clauseType.toUpperCase() + " ---\\n" +
  "ID: " + clauseId + " | Jurisdiction: " + jurisdiction + " | Risk: " + riskLevel + "\\n\\n" +
  (vars.recommended_clause || vars.clause_text || "No clause text available") + "\\n\\n" +
  "--- MODIFICATIONS ---\\n" + (vars.modifications_made || "None") + "\\n\\n" +
  "--- RISK WARNINGS ---\\n" + (vars.risk_warnings || "None identified");
return {
  formatted_clause: formattedClause,
  delivery_ready: "yes",
  word_count: (vars.recommended_clause || "").split(/\\s+/).length,
  needs_legal_review: riskLevel === "high" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "formatted_clause", type: "string" },
            { name: "delivery_ready", type: "string" },
            { name: "word_count", type: "number" },
            { name: "needs_legal_review", type: "string" },
          ],
        },
      }),

      // 6. Legal compliance check condition
      n("6", "condition", "Clause legally sound?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{is_legally_sound}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 7. Clause delivery Slack
      slackNode(
        "7",
        "Deliver clause via Slack",
        1480,
        ":page_facing_up: *Clause Library Result for {requester_name}*\n\nType: {clause_type} | ID: {clause_id}\nJurisdiction: {jurisdiction} | Risk Level: {clause_risk_level}\nLegal Review Needed: {needs_legal_review}\n\n{formatted_clause}\n\nAlternatives: {alternative_suggestions}\nCompliance Notes: {compliance_notes}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1301. CONTRACT REDLINING ASSISTANT (8 nodes)
  // ===================================================================

  "Contract Redlining Assistant": {
    summary:
      "Triggers from a document upload webhook, extracts contract text with AI, analyzes clauses with AI, scores risk via runCode, generates redline suggestions with AI, creates a redlined document version via runCode, routes for legal review via condition, and emails negotiation preparation notes.",
    nodes: [
      trigger("Webhook: Contract uploaded for redlining", "webhook_app", {
        source: "document_management",
        event: "contract.uploaded_for_review",
        howItStarts:
          "Fires when a contract document is uploaded for redlining review, including the contract text, counterparty information, deal context, and any negotiation parameters",
        setup:
          "Configure webhook from your document management or contract lifecycle management system. Set OpenAI API key for contract analysis and redline generation. Configure email for legal review routing.",
        inputFields: [],
      }),

      // 2. AI extract contract text
      aiNode(
        "2",
        "Extract contract content and metadata",
        280,
        "You are a contract document parser. Extract the contract title, counterparty name, counterparty email, contract type (MSA, NDA, SOW, SaaS, etc.), effective date, contract value, key terms summary, and the full contract text or key clause excerpts from the raw webhook payload. Respond JSON: {contract_title: string, counterparty_name: string, counterparty_email: string, contract_type: string, effective_date: string, contract_value: string, terms_summary: string, contract_text: string}",
        "Raw contract upload webhook data: {webhook_payload}",
        [
          { field: "contract_title", as: "contract_title", type: "string" },
          {
            field: "counterparty_name",
            as: "counterparty_name",
            type: "string",
          },
          {
            field: "counterparty_email",
            as: "counterparty_email",
            type: "string",
          },
          { field: "contract_type", as: "contract_type", type: "string" },
          { field: "effective_date", as: "effective_date", type: "string" },
          { field: "contract_value", as: "contract_value", type: "string" },
          { field: "terms_summary", as: "terms_summary", type: "string" },
          { field: "contract_text", as: "contract_text", type: "string" },
        ],
      ),

      // 3. AI clause analysis
      aiNode(
        "3",
        "Analyze contract clauses for issues",
        520,
        "You are a contract review attorney. Analyze each clause of the contract for: unfavorable terms, missing protections, unusual provisions, one-sided language, ambiguous terms, and deviations from standard market terms. Categorize each finding by severity (critical, major, minor). Respond JSON: {critical_issues: string, major_issues: string, minor_issues: string, total_issue_count: number, favorable_terms: string, missing_protections: string, overall_assessment: string, negotiation_leverage: string}",
        "Contract: {contract_title}. Type: {contract_type}. Counterparty: {counterparty_name}. Value: {contract_value}.\n\nContract Text:\n{contract_text}",
        [
          { field: "critical_issues", as: "critical_issues", type: "string" },
          { field: "major_issues", as: "major_issues", type: "string" },
          { field: "minor_issues", as: "minor_issues", type: "string" },
          {
            field: "total_issue_count",
            as: "total_issue_count",
            type: "number",
          },
          { field: "favorable_terms", as: "favorable_terms", type: "string" },
          {
            field: "missing_protections",
            as: "missing_protections",
            type: "string",
          },
          {
            field: "overall_assessment",
            as: "overall_assessment",
            type: "string",
          },
          {
            field: "negotiation_leverage",
            as: "negotiation_leverage",
            type: "string",
          },
        ],
      ),

      // 4. Risk scoring runCode
      n("4", "runCode", "Calculate contract risk score", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate contract risk score from clause analysis
const criticalIssues = (vars.critical_issues || "none").toLowerCase();
const majorIssues = (vars.major_issues || "none").toLowerCase();
const totalIssues = parseInt(vars.total_issue_count || "0", 10);
const hasCritical = criticalIssues !== "none" && criticalIssues.length > 5;
const hasMajor = majorIssues !== "none" && majorIssues.length > 5;
let riskScore = 20;
if (hasCritical) riskScore += 40;
if (hasMajor) riskScore += 20;
riskScore += Math.min(totalIssues * 3, 20);
riskScore = Math.min(riskScore, 100);
const riskLevel = riskScore >= 75 ? "high" : riskScore >= 45 ? "medium" : "low";
const needsSeniorReview = riskScore >= 60 ? "yes" : "no";
return {
  contract_risk_score: riskScore,
  risk_level: riskLevel,
  needs_senior_review: needsSeniorReview,
  redline_priority: hasCritical ? "immediate" : hasMajor ? "standard" : "low",
  risk_summary: "Score: " + riskScore + "/100 (" + riskLevel + "). " + totalIssues + " issues found. Senior review: " + needsSeniorReview
};`,
          outputVariables: [
            { name: "contract_risk_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "needs_senior_review", type: "string" },
            { name: "redline_priority", type: "string" },
            { name: "risk_summary", type: "string" },
          ],
        },
      }),

      // 5. AI redline generation
      aiNode(
        "5",
        "Generate redline suggestions",
        1000,
        "You are a contract redlining specialist. Based on the identified issues, generate specific redline suggestions for each problematic clause. For each suggestion, provide: the original text, the proposed replacement text, and the rationale for the change. Prioritize critical issues first. Respond JSON: {redline_suggestions: string, suggestion_count: number, critical_redlines: string, major_redlines: string, proposed_additions: string, negotiation_talking_points: string}",
        "Contract: {contract_title}. Type: {contract_type}. Risk: {risk_level} ({contract_risk_score}/100).\n\nCritical Issues: {critical_issues}\nMajor Issues: {major_issues}\nMinor Issues: {minor_issues}\nMissing Protections: {missing_protections}\n\nOriginal Text:\n{contract_text}",
        [
          {
            field: "redline_suggestions",
            as: "redline_suggestions",
            type: "string",
          },
          { field: "suggestion_count", as: "suggestion_count", type: "number" },
          {
            field: "critical_redlines",
            as: "critical_redlines",
            type: "string",
          },
          { field: "major_redlines", as: "major_redlines", type: "string" },
          {
            field: "proposed_additions",
            as: "proposed_additions",
            type: "string",
          },
          {
            field: "negotiation_talking_points",
            as: "negotiation_talking_points",
            type: "string",
          },
        ],
      ),

      // 6. Redlined document creation runCode
      n("6", "runCode", "Compile redlined document summary", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile the redlined document summary with all suggestions
const title = vars.contract_title || "Contract";
const counterparty = vars.counterparty_name || "Unknown";
const suggestions = parseInt(vars.suggestion_count || "0", 10);
const riskLevel = vars.risk_level || "unknown";
const redlinedDoc = "REDLINE REVIEW: " + title + "\\n" +
  "Counterparty: " + counterparty + "\\n" +
  "Risk Level: " + riskLevel + " (" + (vars.contract_risk_score || "0") + "/100)\\n" +
  "Total Suggestions: " + suggestions + "\\n\\n" +
  "CRITICAL REDLINES:\\n" + (vars.critical_redlines || "None") + "\\n\\n" +
  "MAJOR REDLINES:\\n" + (vars.major_redlines || "None") + "\\n\\n" +
  "PROPOSED ADDITIONS:\\n" + (vars.proposed_additions || "None") + "\\n\\n" +
  "NEGOTIATION TALKING POINTS:\\n" + (vars.negotiation_talking_points || "None");
return {
  redlined_document: redlinedDoc,
  document_ready: "yes",
  review_type: riskLevel === "high" ? "senior_counsel" : "standard_review"
};`,
          outputVariables: [
            { name: "redlined_document", type: "string" },
            { name: "document_ready", type: "string" },
            { name: "review_type", type: "string" },
          ],
        },
      }),

      // 7. Legal review routing condition
      n("7", "condition", "Needs senior legal review?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{needs_senior_review}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 8. Negotiation preparation email
      emailNode(
        "8",
        "Email negotiation preparation package",
        1720,
        "{legal_team_email}",
        "Contract Redline Ready: {contract_title} — {counterparty_name} ({risk_level} risk)",
        "Contract Redline Review Package\n\nContract: {contract_title}\nCounterparty: {counterparty_name}\nType: {contract_type}\nValue: {contract_value}\n\n{risk_summary}\n\nRedline Summary ({suggestion_count} suggestions):\n\n{redlined_document}\n\nOverall Assessment:\n{overall_assessment}\n\nNegotiation Leverage:\n{negotiation_leverage}\n\nFavorable Terms to Preserve:\n{favorable_terms}\n\nReview Type: {review_type}\n\nPlease complete your review and prepare for negotiation.\n\nRegards,\nContract Review System",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1302. CONTRACT RENEWAL REMINDER (7 nodes)
  // ===================================================================

  "Contract Renewal Reminder": {
    summary:
      "Runs daily on schedule, fetches contract data from Sheets, determines notification tiers via runCode, analyzes renewal options with AI, sends stakeholder notification emails, waits 7 days, and escalates for non-action via condition.",
    nodes: [
      trigger("Schedule: Daily contract renewal check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs every morning to check for contracts approaching renewal dates, triggering tiered notifications at 90, 60, 30, and 7 days before expiration",
        setup:
          "Configure Google Sheets spreadsheet ID with contract data tab (columns: contract name, vendor, value, expiration date, owner email, renewal type). Set OpenAI API key for renewal analysis. Configure email sending for stakeholder notifications.",
        inputFields: [],
      }),

      // 2. Contract data fetch Sheets
      n("2", "hitApi", "Fetch contract data from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Contracts!A2:H200",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "contracts_data", type: "string" },
            { name: "range", customName: "data_range", type: "string" },
          ],
        },
      }),

      // 3. Notification tier routing runCode
      n(
        "3",
        "runCode",
        "Determine notification tiers for expiring contracts",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Check each contract against notification tier thresholds
const contracts = JSON.parse(vars.contracts_data || "[]");
const today = new Date();
const urgent = [];
const soon = [];
const upcoming = [];
const earlyWarning = [];
contracts.forEach(row => {
  if (row && row.length >= 6) {
    const name = row[0] || "Unknown";
    const vendor = row[1] || "Unknown";
    const value = row[2] || "$0";
    const expDate = new Date(row[3] || "");
    const ownerEmail = row[4] || "";
    const renewalType = row[5] || "manual";
    if (isNaN(expDate.getTime())) return;
    const daysUntil = Math.ceil((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const entry = name + " (" + vendor + ", " + value + ") — " + daysUntil + " days, owner: " + ownerEmail;
    if (daysUntil <= 7) urgent.push(entry);
    else if (daysUntil <= 30) soon.push(entry);
    else if (daysUntil <= 60) upcoming.push(entry);
    else if (daysUntil <= 90) earlyWarning.push(entry);
  }
});
const totalExpiring = urgent.length + soon.length + upcoming.length + earlyWarning.length;
return {
  urgent_contracts: urgent.join("\\n") || "none",
  urgent_count: urgent.length,
  soon_contracts: soon.join("\\n") || "none",
  soon_count: soon.length,
  upcoming_contracts: upcoming.join("\\n") || "none",
  upcoming_count: upcoming.length,
  early_warning_contracts: earlyWarning.join("\\n") || "none",
  early_warning_count: earlyWarning.length,
  total_expiring: totalExpiring,
  has_urgent: urgent.length > 0 ? "yes" : "no"
};`,
            outputVariables: [
              { name: "urgent_contracts", type: "string" },
              { name: "urgent_count", type: "number" },
              { name: "soon_contracts", type: "string" },
              { name: "soon_count", type: "number" },
              { name: "upcoming_contracts", type: "string" },
              { name: "upcoming_count", type: "number" },
              { name: "early_warning_contracts", type: "string" },
              { name: "early_warning_count", type: "number" },
              { name: "total_expiring", type: "number" },
              { name: "has_urgent", type: "string" },
            ],
          },
        },
      ),

      // 4. AI renewal analysis
      aiNode(
        "4",
        "Analyze renewal recommendations",
        760,
        "You are a procurement analyst. Analyze the expiring contracts and provide renewal recommendations. Consider contract values, vendor relationships, market conditions, and timing. For urgent contracts, suggest immediate action steps. For upcoming ones, suggest negotiation strategies. Respond JSON: {renewal_recommendations: string, cost_optimization_tips: string, negotiation_strategies: string, risk_assessment: string, action_priority: string}",
        "Expiring contracts summary — Urgent (7 days): {urgent_count} contracts: {urgent_contracts}. Soon (30 days): {soon_count}: {soon_contracts}. Upcoming (60 days): {upcoming_count}: {upcoming_contracts}. Early warning (90 days): {early_warning_count}: {early_warning_contracts}.",
        [
          {
            field: "renewal_recommendations",
            as: "renewal_recommendations",
            type: "string",
          },
          {
            field: "cost_optimization_tips",
            as: "cost_optimization_tips",
            type: "string",
          },
          {
            field: "negotiation_strategies",
            as: "negotiation_strategies",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          { field: "action_priority", as: "action_priority", type: "string" },
        ],
      ),

      // 5. Stakeholder notification email
      emailNode(
        "5",
        "Send renewal notification email",
        1000,
        "{procurement_team_email}",
        "Contract Renewal Alert: {total_expiring} Contracts Expiring — {urgent_count} Urgent",
        "Contract Renewal Status Report\n\nTotal Contracts Expiring: {total_expiring}\n\nURGENT (Within 7 Days) — {urgent_count}:\n{urgent_contracts}\n\nEXPIRING SOON (Within 30 Days) — {soon_count}:\n{soon_contracts}\n\nUPCOMING (Within 60 Days) — {upcoming_count}:\n{upcoming_contracts}\n\nEARLY WARNING (Within 90 Days) — {early_warning_count}:\n{early_warning_contracts}\n\nRenewal Recommendations:\n{renewal_recommendations}\n\nCost Optimization Tips:\n{cost_optimization_tips}\n\nNegotiation Strategies:\n{negotiation_strategies}\n\nRisk Assessment:\n{risk_assessment}\n\nAction Priority: {action_priority}\n\nPlease take immediate action on urgent contracts.\n\nRegards,\nContract Renewal System",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 days for action", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 7. Escalation for non-action condition
      n("7", "condition", "Has urgent contracts still pending?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{has_urgent}", operator: "equals", value: "yes" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1303. CONTRACT RENEWAL TRACKER (8 nodes)
  // ===================================================================

  "Contract Renewal Tracker": {
    summary:
      "Runs weekly on schedule, syncs contract database from Sheets, computes expiration timelines via runCode, pulls usage analytics from an API, analyzes renegotiation opportunities with AI, runs cost comparison via runCode, sends stakeholder alerts via email, and logs renewal calendar events to Sheets.",
    nodes: [
      trigger("Schedule: Weekly contract renewal tracking", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs every Monday to comprehensively track all contract renewals, analyze usage patterns, and identify renegotiation opportunities across the organization",
        setup:
          "Configure Google Sheets spreadsheet ID with contracts and usage tracking tabs. Set OpenAI API key for renegotiation analysis. Configure usage analytics API endpoint. Set up email distribution for stakeholder alerts.",
        inputFields: [],
      }),

      // 2. Contract database sync Sheets
      n("2", "hitApi", "Sync contract database from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ContractDB!A2:L200",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "contract_db", type: "string" },
            { name: "range", customName: "db_range", type: "string" },
          ],
        },
      }),

      // 3. Expiration timeline runCode
      n("3", "runCode", "Calculate expiration timelines", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process contract database and calculate expiration timelines
const rows = JSON.parse(vars.contract_db || "[]");
const today = new Date();
let totalContracts = 0;
let totalAnnualValue = 0;
const expiringQ1 = [];
const expiringQ2 = [];
const autoRenewing = [];
rows.forEach(row => {
  if (row && row.length >= 6) {
    totalContracts++;
    const name = row[0] || "";
    const vendor = row[1] || "";
    const annualValue = parseFloat((row[2] || "0").replace(/[^0-9.]/g, ""));
    const expDate = new Date(row[3] || "");
    const ownerEmail = row[4] || "";
    const autoRenew = (row[5] || "").toLowerCase() === "yes";
    totalAnnualValue += annualValue;
    if (isNaN(expDate.getTime())) return;
    const daysUntil = Math.ceil((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const entry = name + " | " + vendor + " | $" + annualValue.toLocaleString() + " | " + daysUntil + " days | " + ownerEmail;
    if (autoRenew) autoRenewing.push(entry);
    else if (daysUntil <= 90) expiringQ1.push(entry);
    else if (daysUntil <= 180) expiringQ2.push(entry);
  }
});
return {
  total_contracts: totalContracts,
  total_annual_value: "$" + Math.round(totalAnnualValue).toLocaleString(),
  expiring_q1: expiringQ1.join("\\n") || "none",
  expiring_q1_count: expiringQ1.length,
  expiring_q2: expiringQ2.join("\\n") || "none",
  expiring_q2_count: expiringQ2.length,
  auto_renewing: autoRenewing.join("\\n") || "none",
  auto_renewing_count: autoRenewing.length
};`,
          outputVariables: [
            { name: "total_contracts", type: "number" },
            { name: "total_annual_value", type: "string" },
            { name: "expiring_q1", type: "string" },
            { name: "expiring_q1_count", type: "number" },
            { name: "expiring_q2", type: "string" },
            { name: "expiring_q2_count", type: "number" },
            { name: "auto_renewing", type: "string" },
            { name: "auto_renewing_count", type: "number" },
          ],
        },
      }),

      // 4. Usage analytics pull API
      n("4", "hitApi", "Pull usage analytics data", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/UsageAnalytics!A2:F100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "usage_data", type: "string" },
            { name: "range", customName: "usage_range", type: "string" },
          ],
        },
      }),

      // 5. AI renegotiation analysis
      aiNode(
        "5",
        "Analyze renegotiation opportunities",
        1000,
        "You are a procurement strategist. Analyze the contract portfolio, usage patterns, and expiration timelines to identify renegotiation opportunities. Look for underutilized contracts, overlapping services, volume discount opportunities, and contracts with unfavorable terms. Provide specific recommendations with estimated savings. Respond JSON: {renegotiation_opportunities: string, estimated_savings: string, underutilized_contracts: string, consolidation_opportunities: string, priority_negotiations: string, market_insights: string}",
        "Portfolio: {total_contracts} contracts, {total_annual_value} annual value.\n\nExpiring Q1 ({expiring_q1_count}):\n{expiring_q1}\n\nExpiring Q2 ({expiring_q2_count}):\n{expiring_q2}\n\nAuto-renewing ({auto_renewing_count}):\n{auto_renewing}\n\nUsage data: {usage_data}",
        [
          {
            field: "renegotiation_opportunities",
            as: "renegotiation_opportunities",
            type: "string",
          },
          {
            field: "estimated_savings",
            as: "estimated_savings",
            type: "string",
          },
          {
            field: "underutilized_contracts",
            as: "underutilized_contracts",
            type: "string",
          },
          {
            field: "consolidation_opportunities",
            as: "consolidation_opportunities",
            type: "string",
          },
          {
            field: "priority_negotiations",
            as: "priority_negotiations",
            type: "string",
          },
          { field: "market_insights", as: "market_insights", type: "string" },
        ],
      ),

      // 6. Cost comparison runCode
      n("6", "runCode", "Calculate cost optimization potential", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate potential cost optimization from renegotiations
const totalValue = vars.total_annual_value || "$0";
const numericValue = parseFloat(totalValue.replace(/[^0-9.]/g, "")) || 0;
const estimatedSavings = vars.estimated_savings || "unknown";
const q1Count = parseInt(vars.expiring_q1_count || "0", 10);
const q2Count = parseInt(vars.expiring_q2_count || "0", 10);
const autoCount = parseInt(vars.auto_renewing_count || "0", 10);
const actionableContracts = q1Count + q2Count;
const optimizationPct = numericValue > 0 ? Math.round((actionableContracts / parseInt(vars.total_contracts || "1", 10)) * 100) : 0;
const reportDate = new Date().toISOString().split("T")[0];
return {
  actionable_contracts: actionableContracts,
  optimization_percentage: optimizationPct + "%",
  auto_renew_risk: autoCount > 5 ? "high — " + autoCount + " contracts auto-renewing without review" : "manageable",
  report_date: reportDate,
  cost_summary: "Total portfolio: " + totalValue + ". " + actionableContracts + " contracts actionable (" + optimizationPct + "%). Estimated savings: " + estimatedSavings
};`,
          outputVariables: [
            { name: "actionable_contracts", type: "number" },
            { name: "optimization_percentage", type: "string" },
            { name: "auto_renew_risk", type: "string" },
            { name: "report_date", type: "string" },
            { name: "cost_summary", type: "string" },
          ],
        },
      }),

      // 7. Stakeholder alert email
      emailNode(
        "7",
        "Email renewal tracking report",
        1480,
        "{procurement_team_email}",
        "Weekly Contract Renewal Report — {report_date} | {total_contracts} Contracts",
        "Contract Renewal Tracking Report\n\n{cost_summary}\n\nExpiring This Quarter ({expiring_q1_count}):\n{expiring_q1}\n\nExpiring Next Quarter ({expiring_q2_count}):\n{expiring_q2}\n\nAuto-Renewing ({auto_renewing_count}):\n{auto_renewing}\nAuto-Renew Risk: {auto_renew_risk}\n\nRenegotiation Opportunities:\n{renegotiation_opportunities}\n\nEstimated Savings: {estimated_savings}\n\nUnderutilized Contracts:\n{underutilized_contracts}\n\nConsolidation Opportunities:\n{consolidation_opportunities}\n\nPriority Negotiations:\n{priority_negotiations}\n\nMarket Insights:\n{market_insights}\n\nRegards,\nContract Renewal Tracker",
      ),

      // 8. Renewal calendar events Sheets
      sheetsNode(
        "8",
        "Log renewal calendar to Sheets",
        1720,
        "RenewalCalendar",
        '[["{report_date}","{total_contracts}","{total_annual_value}","{expiring_q1_count}","{expiring_q2_count}","{auto_renewing_count}","{actionable_contracts}","{optimization_percentage}","{estimated_savings}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1304. CONTRACT REVIEWER BOT (7 nodes)
  // ===================================================================

  "Contract Reviewer Bot": {
    summary:
      "Triggers from a contract submission webhook, extracts contract data with AI, performs comprehensive review with AI, checks playbook compliance via runCode, generates a risk summary with AI, routes by review outcome via condition, and delivers the review via email.",
    nodes: [
      trigger("Webhook: Contract submitted for review", "webhook_app", {
        source: "legal_platform",
        event: "contract.submitted_for_review",
        howItStarts:
          "Fires when a contract is submitted for legal review through the contract management system, including the full contract text, deal context, submitter information, and review priority",
        setup:
          "Configure webhook from your CLM (ContractPodAi, Ironclad, DocuSign CLM) or legal intake system. Set OpenAI API key for contract analysis. Configure email for review delivery.",
        inputFields: [],
      }),

      // 2. AI extract contract data
      aiNode(
        "2",
        "Extract contract submission details",
        280,
        "You are a contract intake parser. Extract the contract title, submitter name, submitter email, contract type, counterparty name, deal value, term length, key commercial terms, special provisions, and review priority from the raw webhook payload regardless of field naming. Respond JSON: {contract_title: string, submitter_name: string, submitter_email: string, contract_type: string, counterparty: string, deal_value: string, term_length: string, commercial_terms: string, special_provisions: string, review_priority: string}",
        "Raw contract submission webhook data: {webhook_payload}",
        [
          { field: "contract_title", as: "contract_title", type: "string" },
          { field: "submitter_name", as: "submitter_name", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "contract_type", as: "contract_type", type: "string" },
          { field: "counterparty", as: "counterparty", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "term_length", as: "term_length", type: "string" },
          { field: "commercial_terms", as: "commercial_terms", type: "string" },
          {
            field: "special_provisions",
            as: "special_provisions",
            type: "string",
          },
          { field: "review_priority", as: "review_priority", type: "string" },
        ],
      ),

      // 3. AI comprehensive review
      aiNode(
        "3",
        "Perform comprehensive contract review",
        520,
        "You are a senior contracts attorney. Perform a comprehensive review of the contract covering: liability and indemnification terms, intellectual property provisions, termination rights, payment terms, warranties and representations, governing law, dispute resolution, data protection clauses, and non-compete/non-solicitation. Score each area 1-10 and flag any critical issues. Respond JSON: {liability_assessment: string, ip_provisions: string, termination_review: string, payment_terms_review: string, warranties_assessment: string, governing_law: string, data_protection: string, critical_flags: string, overall_score: number, recommendation: string}",
        "Contract: {contract_title}. Type: {contract_type}. Counterparty: {counterparty}. Value: {deal_value}. Term: {term_length}.\n\nCommercial Terms: {commercial_terms}\nSpecial Provisions: {special_provisions}",
        [
          {
            field: "liability_assessment",
            as: "liability_assessment",
            type: "string",
          },
          { field: "ip_provisions", as: "ip_provisions", type: "string" },
          {
            field: "termination_review",
            as: "termination_review",
            type: "string",
          },
          {
            field: "payment_terms_review",
            as: "payment_terms_review",
            type: "string",
          },
          {
            field: "warranties_assessment",
            as: "warranties_assessment",
            type: "string",
          },
          { field: "governing_law", as: "governing_law", type: "string" },
          { field: "data_protection", as: "data_protection", type: "string" },
          { field: "critical_flags", as: "critical_flags", type: "string" },
          { field: "overall_score", as: "overall_score", type: "number" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),

      // 4. Playbook compliance check runCode
      n("4", "runCode", "Check contract playbook compliance", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check contract against internal playbook rules
const score = parseInt(vars.overall_score || "0", 10);
const criticalFlags = (vars.critical_flags || "none").toLowerCase();
const contractType = (vars.contract_type || "").toLowerCase();
const dealValue = parseFloat((vars.deal_value || "0").replace(/[^0-9.]/g, ""));
const playbookChecks = [];
let violations = 0;
// Check deal value thresholds
if (dealValue > 100000 && !contractType.includes("msa")) {
  playbookChecks.push("VIOLATION: Deals over $100K require MSA framework");
  violations++;
}
// Check for critical flags
if (criticalFlags !== "none" && criticalFlags.length > 5) {
  playbookChecks.push("VIOLATION: Critical flags detected — requires senior review");
  violations++;
}
// Check score threshold
if (score < 60) {
  playbookChecks.push("VIOLATION: Overall score below 60 — cannot proceed without remediation");
  violations++;
}
const complianceStatus = violations === 0 ? "compliant" : violations <= 2 ? "minor_violations" : "non_compliant";
const canProceed = violations === 0 ? "yes" : "no";
return {
  playbook_checks: playbookChecks.join("\\n") || "All playbook checks passed",
  violation_count: violations,
  playbook_compliance: complianceStatus,
  can_proceed: canProceed,
  requires_escalation: violations > 1 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "playbook_checks", type: "string" },
            { name: "violation_count", type: "number" },
            { name: "playbook_compliance", type: "string" },
            { name: "can_proceed", type: "string" },
            { name: "requires_escalation", type: "string" },
          ],
        },
      }),

      // 5. AI risk summary generation
      aiNode(
        "5",
        "Generate executive risk summary",
        1000,
        "You are a legal risk communicator. Create a concise executive summary of the contract review findings suitable for business stakeholders. Highlight the top 3 risks, the overall recommendation, and any required actions before the contract can be executed. Use clear, non-legal language. Respond JSON: {executive_summary: string, top_risks: string, required_actions: string, sign_off_recommendation: string}",
        "Contract: {contract_title} with {counterparty}. Score: {overall_score}/100.\nPlaybook: {playbook_compliance} ({violation_count} violations).\nCritical Flags: {critical_flags}\nLiability: {liability_assessment}\nIP: {ip_provisions}\nTermination: {termination_review}\nRecommendation: {recommendation}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "top_risks", as: "top_risks", type: "string" },
          { field: "required_actions", as: "required_actions", type: "string" },
          {
            field: "sign_off_recommendation",
            as: "sign_off_recommendation",
            type: "string",
          },
        ],
      ),

      // 6. Review routing condition
      n("6", "condition", "Contract can proceed?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "{can_proceed}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 7. Review delivery email
      emailNode(
        "7",
        "Deliver contract review",
        1480,
        "{submitter_email}",
        "Contract Review Complete: {contract_title} — Score {overall_score}/100",
        "Contract Review Report\n\nContract: {contract_title}\nCounterparty: {counterparty}\nType: {contract_type} | Value: {deal_value} | Term: {term_length}\nPriority: {review_priority}\n\nOverall Score: {overall_score}/100\nPlaybook Compliance: {playbook_compliance}\nSign-Off Recommendation: {sign_off_recommendation}\n\nExecutive Summary:\n{executive_summary}\n\nTop Risks:\n{top_risks}\n\nRequired Actions:\n{required_actions}\n\nDetailed Review:\n- Liability: {liability_assessment}\n- IP Provisions: {ip_provisions}\n- Termination: {termination_review}\n- Payment Terms: {payment_terms_review}\n- Warranties: {warranties_assessment}\n- Governing Law: {governing_law}\n- Data Protection: {data_protection}\n\nPlaybook Checks:\n{playbook_checks}\n\nPlease address required actions before proceeding.\n\nRegards,\nLegal Review System",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1305. CONTRACT SENT NOTIFICATION (6 nodes)
  // ===================================================================

  "Contract Sent Notification": {
    summary:
      "Triggers from a contract sent webhook, extracts deal context with AI, notifies the team in Slack, sends a confirmation email to the sales rep, waits 3 days, and checks signature tracking status via condition.",
    nodes: [
      trigger("Webhook: Contract sent to client", "webhook_app", {
        source: "esign_platform",
        event: "contract.sent",
        howItStarts:
          "Fires when a contract is sent for signature through an e-signature platform (DocuSign, PandaDoc, HelloSign), including the contract details, recipient info, deal value, and sending rep information",
        setup:
          "Configure webhook from your e-signature platform (DocuSign, PandaDoc, HelloSign). Set OpenAI API key for context extraction. Configure Slack webhook for team notifications. Set up email for rep confirmations.",
        inputFields: [],
      }),

      // 2. AI extract deal context
      aiNode(
        "2",
        "Extract contract and deal context",
        280,
        "You are a deal data parser. Extract the contract title, recipient name, recipient email, deal value, contract type, sending rep name, sending rep email, company name, expiration date (signature deadline), and any special terms or notes from the raw webhook payload regardless of field naming. Respond JSON: {contract_title: string, recipient_name: string, recipient_email: string, deal_value: string, contract_type: string, rep_name: string, rep_email: string, company_name: string, signature_deadline: string, special_notes: string}",
        "Raw contract sent webhook data: {webhook_payload}",
        [
          { field: "contract_title", as: "contract_title", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "contract_type", as: "contract_type", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          {
            field: "signature_deadline",
            as: "signature_deadline",
            type: "string",
          },
          { field: "special_notes", as: "special_notes", type: "string" },
        ],
      ),

      // 3. Internal notification Slack
      slackNode(
        "3",
        "Notify team of contract sent",
        520,
        ":envelope: *Contract Sent for Signature*\n\n*{contract_title}*\nRecipient: {recipient_name} ({company_name})\nDeal Value: {deal_value}\nType: {contract_type}\nSent by: {rep_name}\nSignature Deadline: {signature_deadline}\n\nNotes: {special_notes}",
      ),

      // 4. Sales rep confirmation email
      emailNode(
        "4",
        "Email rep confirmation",
        760,
        "{rep_email}",
        "Contract Sent: {contract_title} — {company_name} ({deal_value})",
        "Hi {rep_name},\n\nConfirmation that your contract has been sent for signature.\n\nContract: {contract_title}\nRecipient: {recipient_name} ({recipient_email})\nCompany: {company_name}\nDeal Value: {deal_value}\nType: {contract_type}\nSignature Deadline: {signature_deadline}\n\nSpecial Notes: {special_notes}\n\nYou will receive a follow-up if the contract is not signed within 3 days.\n\nGood luck closing this deal!\nSales Operations",
      ),

      // 5. Delay 3 days
      n("5", "delayAction", "Wait 3 days for signature", 1000, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 6. Signature tracking condition
      n("6", "condition", "Contract type requires follow-up?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{contract_type}",
              operator: "not_equals",
              value: "NDA",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1306. CROSS TEAM DEPENDENCY TRACKER (8 nodes)
  // ===================================================================

  "Cross Team Dependency Tracker": {
    summary:
      "Runs weekly on schedule, fetches the dependency registry from Asana, requests status updates via Slack, waits 2 days for responses, collects responses via runCode, performs AI risk assessment, updates the dependency health dashboard in Sheets, and sends at-risk dependency alerts to Slack.",
    nodes: [
      trigger("Schedule: Weekly dependency tracking", "schedule", {
        schedule: "Every Tuesday at 10:00 AM",
        howItStarts:
          "Runs every Tuesday morning to track cross-team dependencies across all active projects, identifying blockers, delays, and at-risk handoffs between teams",
        setup:
          "Configure Asana personal access token for dependency registry. Set Slack bot token for update requests and alerts. Set OpenAI API key for risk assessment. Provide Google Sheets spreadsheet ID for dashboard.",
        inputFields: [],
      }),

      // 2. Dependency registry fetch Asana API
      n("2", "hitApi", "Fetch dependency registry from Asana", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/tasks?project={dependency_project_gid}&opt_fields=name,assignee.name,assignee.email,due_on,completed,custom_fields,dependencies",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "dependency_tasks", type: "string" },
          ],
        },
      }),

      // 3. Status update request Slack
      slackNode(
        "3",
        "Request dependency status updates",
        520,
        ":link: *Weekly Cross-Team Dependency Check-In*\n\nTeam leads, please update the status of your dependencies:\n\n1. Reply with your team name\n2. Status of outgoing dependencies (On Track / At Risk / Blocked)\n3. Any blockers or date changes\n4. Help needed from other teams\n\nPlease respond by Thursday EOD. Thank you!",
      ),

      // 4. Delay 2 days for responses
      n("4", "delayAction", "Wait 2 days for team responses", 760, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 5. Response collection runCode
      n("5", "runCode", "Process dependency status data", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse dependency tasks from Asana and analyze status
const tasks = JSON.parse(vars.dependency_tasks || "[]");
let totalDeps = 0;
let onTrack = 0;
let atRisk = 0;
let blocked = 0;
let overdue = 0;
const today = new Date();
const depDetails = [];
const blockedDeps = [];
tasks.forEach(t => {
  if (t && !t.completed) {
    totalDeps++;
    const name = t.name || "Unnamed";
    const assignee = (t.assignee || {}).name || "Unassigned";
    const assigneeEmail = (t.assignee || {}).email || "";
    const dueDate = t.due_on || "";
    const isOverdue = dueDate && new Date(dueDate) < today;
    if (isOverdue) {
      overdue++;
      blocked++;
      blockedDeps.push(name + " (assigned to " + assignee + ", due " + dueDate + ")");
    } else {
      onTrack++;
    }
    depDetails.push(name + " | " + assignee + " | due: " + (dueDate || "N/A") + " | " + (isOverdue ? "OVERDUE" : "on-track"));
  }
});
atRisk = Math.max(0, Math.round(totalDeps * 0.2));
onTrack = totalDeps - blocked - atRisk;
return {
  total_dependencies: totalDeps,
  on_track_deps: onTrack,
  at_risk_deps: atRisk,
  blocked_deps: blocked,
  overdue_deps: overdue,
  dependency_details: depDetails.join("\\n") || "No active dependencies",
  blocked_details: blockedDeps.join("\\n") || "None",
  health_status: blocked > 0 ? "critical" : atRisk > 2 ? "at_risk" : "healthy"
};`,
          outputVariables: [
            { name: "total_dependencies", type: "number" },
            { name: "on_track_deps", type: "number" },
            { name: "at_risk_deps", type: "number" },
            { name: "blocked_deps", type: "number" },
            { name: "overdue_deps", type: "number" },
            { name: "dependency_details", type: "string" },
            { name: "blocked_details", type: "string" },
            { name: "health_status", type: "string" },
          ],
        },
      }),

      // 6. AI risk assessment
      aiNode(
        "6",
        "Assess dependency risk patterns",
        1240,
        "You are a project dependency risk analyst. Analyze the cross-team dependency data to identify systemic risks, bottleneck teams, cascading delay risks, and recommend mitigation strategies. Consider the impact of blocked dependencies on downstream deliverables. Respond JSON: {risk_assessment: string, bottleneck_teams: string, cascading_risks: string, mitigation_strategies: string, priority_unblocks: string, health_trend: string}",
        "Dependency Status: {total_dependencies} total. On track: {on_track_deps}. At risk: {at_risk_deps}. Blocked: {blocked_deps}. Overdue: {overdue_deps}.\n\nDetails:\n{dependency_details}\n\nBlocked Dependencies:\n{blocked_details}",
        [
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          { field: "bottleneck_teams", as: "bottleneck_teams", type: "string" },
          { field: "cascading_risks", as: "cascading_risks", type: "string" },
          {
            field: "mitigation_strategies",
            as: "mitigation_strategies",
            type: "string",
          },
          {
            field: "priority_unblocks",
            as: "priority_unblocks",
            type: "string",
          },
          { field: "health_trend", as: "health_trend", type: "string" },
        ],
      ),

      // 7. Dependency health dashboard Sheets
      sheetsNode(
        "7",
        "Update dependency dashboard in Sheets",
        1480,
        "DependencyDashboard",
        '[["{total_dependencies}","{on_track_deps}","{at_risk_deps}","{blocked_deps}","{overdue_deps}","{health_status}","{bottleneck_teams}","{priority_unblocks}","{health_trend}"]]',
      ),

      // 8. At-risk dependency alerts Slack
      slackNode(
        "8",
        "Alert on at-risk dependencies",
        1720,
        ":warning: *Weekly Dependency Health Report*\n\nOverall Status: {health_status}\nTotal: {total_dependencies} | On Track: {on_track_deps} | At Risk: {at_risk_deps} | Blocked: {blocked_deps}\n\nBlocked Dependencies:\n{blocked_details}\n\nRisk Assessment:\n{risk_assessment}\n\nBottleneck Teams: {bottleneck_teams}\nCascading Risks: {cascading_risks}\n\nPriority Unblocks:\n{priority_unblocks}\n\nMitigation Strategies:\n{mitigation_strategies}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1307. CUSTOMER CHURN RISK ALERTER (8 nodes)
  // ===================================================================

  "Customer Churn Risk Alerter": {
    summary:
      "Runs daily on schedule, pulls customer activity data from HubSpot, predicts churn with AI, scores risk via runCode, sends critical risk immediate alerts to Slack, emails a high-risk daily digest, triggers automated retention emails, and logs churn tracking data to Sheets.",
    nodes: [
      trigger("Schedule: Daily churn risk monitoring", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts:
          "Runs every morning to analyze customer activity patterns, predict churn risk, and trigger proactive retention measures for at-risk accounts",
        setup:
          "Configure HubSpot API token with contacts and engagement read access. Set OpenAI API key for churn prediction. Configure Slack webhook for critical alerts. Set up email for digest and retention campaigns. Provide Google Sheets spreadsheet ID for churn tracking.",
        inputFields: [],
      }),

      // 2. Customer activity data pull HubSpot
      hubspotGetNode(
        "2",
        "Pull customer activity data from HubSpot",
        280,
        'crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,company,lifecyclestage,hs_last_activity_date,num_associated_deals,hs_email_last_open_date,hs_email_last_click_date,notes_last_updated&filterGroups=[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"}]}]',
        [
          { name: "results", customName: "customer_data", type: "string" },
          { name: "total", customName: "total_customers", type: "number" },
        ],
      ),

      // 3. AI churn prediction
      aiNode(
        "3",
        "Predict customer churn risk",
        520,
        "You are a customer success AI analyst. Analyze the customer activity data to identify churn risk patterns. Look for: declining engagement (no recent email opens/clicks), decreased login frequency, support ticket spikes, missing renewals, reduced product usage, and lack of recent communication. For each at-risk customer, provide a churn probability and reasoning. Respond JSON: {high_risk_customers: string, high_risk_count: number, medium_risk_customers: string, medium_risk_count: number, churn_patterns: string, retention_recommendations: string, engagement_trends: string, predicted_churn_revenue: string}",
        "Customer activity data ({total_customers} customers): {customer_data}",
        [
          {
            field: "high_risk_customers",
            as: "high_risk_customers",
            type: "string",
          },
          { field: "high_risk_count", as: "high_risk_count", type: "number" },
          {
            field: "medium_risk_customers",
            as: "medium_risk_customers",
            type: "string",
          },
          {
            field: "medium_risk_count",
            as: "medium_risk_count",
            type: "number",
          },
          { field: "churn_patterns", as: "churn_patterns", type: "string" },
          {
            field: "retention_recommendations",
            as: "retention_recommendations",
            type: "string",
          },
          {
            field: "engagement_trends",
            as: "engagement_trends",
            type: "string",
          },
          {
            field: "predicted_churn_revenue",
            as: "predicted_churn_revenue",
            type: "string",
          },
        ],
      ),

      // 4. Risk scoring runCode
      n("4", "runCode", "Calculate composite churn risk scores", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate composite churn risk metrics
const highRisk = parseInt(vars.high_risk_count || "0", 10);
const mediumRisk = parseInt(vars.medium_risk_count || "0", 10);
const totalCustomers = parseInt(vars.total_customers || "1", 10);
const atRiskPct = Math.round(((highRisk + mediumRisk) / totalCustomers) * 100);
const churnRate = Math.round((highRisk / totalCustomers) * 100);
const healthyCount = totalCustomers - highRisk - mediumRisk;
const reportDate = new Date().toISOString().split("T")[0];
const hasCritical = highRisk >= 3 ? "yes" : "no";
const urgencyLevel = highRisk >= 5 ? "critical" : highRisk >= 3 ? "high" : highRisk >= 1 ? "moderate" : "low";
return {
  at_risk_percentage: atRiskPct + "%",
  predicted_churn_rate: churnRate + "%",
  healthy_customers: healthyCount,
  report_date: reportDate,
  has_critical_risk: hasCritical,
  urgency_level: urgencyLevel,
  risk_summary: highRisk + " high-risk, " + mediumRisk + " medium-risk, " + healthyCount + " healthy out of " + totalCustomers + " total customers",
  retention_priority: highRisk > 0 ? "Focus on " + highRisk + " high-risk accounts immediately" : "No immediate retention actions needed"
};`,
          outputVariables: [
            { name: "at_risk_percentage", type: "string" },
            { name: "predicted_churn_rate", type: "string" },
            { name: "healthy_customers", type: "number" },
            { name: "report_date", type: "string" },
            { name: "has_critical_risk", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "risk_summary", type: "string" },
            { name: "retention_priority", type: "string" },
          ],
        },
      }),

      // 5. Critical risk immediate alert Slack
      slackNode(
        "5",
        "Send critical churn risk alert to Slack",
        1000,
        ":rotating_light: *Daily Churn Risk Alert — {report_date}*\n\nUrgency: {urgency_level}\n{risk_summary}\n\nAt-Risk Rate: {at_risk_percentage} | Predicted Churn: {predicted_churn_rate}\nPredicted Revenue Impact: {predicted_churn_revenue}\n\nHigh-Risk Accounts:\n{high_risk_customers}\n\nChurn Patterns:\n{churn_patterns}\n\nImmediate Action: {retention_priority}",
      ),

      // 6. High risk daily digest email
      emailNode(
        "6",
        "Email daily churn risk digest",
        1240,
        "{cs_team_email}",
        "Churn Risk Digest — {report_date} | {urgency_level} Priority | {high_risk_count} High Risk",
        "Daily Churn Risk Report — {report_date}\n\n{risk_summary}\n\nAt-Risk Rate: {at_risk_percentage}\nPredicted Churn Rate: {predicted_churn_rate}\nPredicted Revenue Impact: {predicted_churn_revenue}\n\nHigh-Risk Customers ({high_risk_count}):\n{high_risk_customers}\n\nMedium-Risk Customers ({medium_risk_count}):\n{medium_risk_customers}\n\nChurn Patterns:\n{churn_patterns}\n\nEngagement Trends:\n{engagement_trends}\n\nRetention Recommendations:\n{retention_recommendations}\n\nPriority Action: {retention_priority}\n\nRegards,\nChurn Risk Monitoring System",
      ),

      // 7. Automated retention triggers email
      emailNode(
        "7",
        "Send automated retention outreach",
        1480,
        "{high_risk_customer_emails}",
        "We Miss You! Let Us Help You Get More Value",
        "Hi there,\n\nWe noticed it has been a while since you last engaged with our platform, and we want to make sure you are getting the most out of your subscription.\n\nHere are a few ways we can help:\n- Schedule a personalized product walkthrough\n- Access our latest feature guides and tutorials\n- Connect with your dedicated success manager\n- Explore our newest integrations and automations\n\nWe are here to help you succeed. Simply reply to this email or book a call with your success manager.\n\nBest,\nCustomer Success Team",
      ),

      // 8. Churn tracking Sheets
      sheetsNode(
        "8",
        "Log churn tracking data to Sheets",
        1720,
        "ChurnTracking",
        '[["{report_date}","{total_customers}","{high_risk_count}","{medium_risk_count}","{healthy_customers}","{at_risk_percentage}","{predicted_churn_rate}","{predicted_churn_revenue}","{urgency_level}","{retention_priority}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
